import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Pressable, 
  ScrollView,
  TextInput,
  Animated,
  Platform,
  ActivityIndicator,
  Dimensions,
  Modal
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { useAuth } from '../hooks/useAuth';
import { useSheets } from '../hooks/useSheets';
import { useSharing } from '../hooks/useSharing';
import { useNotifications } from '../hooks/useNotifications';
import { useConnections } from '../hooks/useConnections';
import { FREE_TIER } from '../config/stripe';

// SVG Icons as components
const SunIcon = ({ color = '#000', size = 20 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>☀</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
};

const MoonIcon = ({ color = '#000', size = 20 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>☾</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
};

const FolderIcon = ({ color = '#000', size = 18, filled = false }) => {
  if (!Platform.OS === 'web') return <View style={{ width: size, height: size * 0.8, borderWidth: 1.5, borderColor: color, borderRadius: 2 }} />;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
};

const SearchIcon = ({ color = '#000', size = 18 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>⌕</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  );
};

const HeartIcon = ({ color = '#000', size = 18, filled = false }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>{filled ? '♥' : '♡'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  );
};

const StarIcon = ({ color = '#000', size = 18, filled = false }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>{filled ? '★' : '☆'}</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? color : 'none'} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  );
};

const UploadIcon = ({ color = '#000', size = 24 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>↑</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="17,8 12,3 7,8"/>
      <line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
};

const TrashIcon = ({ color = '#000', size = 18 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>🗑</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3,6 5,6 21,6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6"/>
      <path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
};

const MoreIcon = ({ color = '#000', size = 18 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>⋯</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <circle cx="12" cy="12" r="2"/>
      <circle cx="19" cy="12" r="2"/>
      <circle cx="5" cy="12" r="2"/>
    </svg>
  );
};

const ShareIcon = ({ color = '#000', size = 16 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>↗</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"/>
      <circle cx="6" cy="12" r="3"/>
      <circle cx="18" cy="19" r="3"/>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
    </svg>
  );
};

const MusicNoteIcon = ({ color = '#000', size = 32 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>♪</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
    </svg>
  );
};

const CloseIcon = ({ color = '#000', size = 20 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size, fontWeight: 'bold' }}>×</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
};

const PlusIcon = ({ color = '#000', size = 16 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size, fontWeight: 'bold' }}>+</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
};

const BellIcon = ({ color = '#000', size = 20 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>🔔</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
};

const UsersIcon = ({ color = '#000', size = 18 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>👥</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  );
};

const SortIcon = ({ color = '#000', size = 16 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>↕</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="15" y2="12"/>
      <line x1="3" y1="18" x2="9" y2="18"/>
    </svg>
  );
};

const FilterIcon = ({ color = '#000', size = 16 }) => {
  if (!Platform.OS === 'web') return <Text style={{ color, fontSize: size }}>⊟</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/>
    </svg>
  );
};

const BigFolderIcon = ({ color = '#000', size = 56 }) => {
  if (!Platform.OS === 'web') return <View style={{ width: size, height: size * 0.8, borderWidth: 2, borderColor: color, borderRadius: 4 }} />;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" opacity="0.9"/>
    </svg>
  );
};

// Common instruments and composers for dropdown suggestions
const INSTRUMENTS = ['Piano', 'Violin', 'Cello', 'Flute', 'Guitar', 'Clarinet', 'Trumpet', 'Saxophone', 'Voice', 'Drums', 'Organ', 'Harp', 'Viola', 'Bass', 'Oboe'];
const GENRES = ['Classical', 'Jazz', 'Pop', 'Rock', 'Folk', 'Blues', 'Contemporary', 'Baroque', 'Romantic', 'Modern', 'Film/TV', 'Musical Theatre', 'Hymns', 'Christmas'];

const FOLDER_COLORS = [
  '#C75B7A', '#6B9EBF', '#7AB87A', '#E8A84C',
  '#9B7AB8', '#5BA3A3', '#E87060', '#60A8E8',
  '#B87A4E', '#7A8CB8',
];

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Responsive breakpoints
const getBreakpoint = (width) => {
  if (width < 480) return 'mobile';
  if (width < 768) return 'mobileLarge';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Set page title
if (isWeb && typeof document !== 'undefined') {
  document.title = 'Library - SheetShed';
}

// Staccato cursor
const StaccatoCursor = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });

  useEffect(() => {
    if (!isWeb) return;
    const handleMouseMove = (e) => setPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    document.body.style.cursor = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.cursor = 'auto';
    };
  }, []);

  if (!isWeb) return null;

  return (
    <div style={{
      position: 'fixed', left: position.x - 4, top: position.y - 4,
      width: 8, height: 8, borderRadius: '50%', backgroundColor: '#C75B7A',
      pointerEvents: 'none', zIndex: 10000, boxShadow: '0 0 12px rgba(199, 91, 122, 0.5)',
    }} />
  );
};

// Musical decorations background
const MusicalDecorations = ({ isDark }) => {
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  // Inject keyframe CSS via DOM so we never put a <style> text node inside the RN tree
  useEffect(() => {
    if (!isWeb) return;
    const el = document.createElement('style');
    el.id = 'sheetshed-decorations-css';
    el.textContent = `@keyframes gentleFlow{0%,100%{transform:translate(0,0)}50%{transform:translate(-2%,3%)}}`;
    if (!document.getElementById('sheetshed-decorations-css')) {
      document.head.appendChild(el);
    }
    return () => { const s = document.getElementById('sheetshed-decorations-css'); if (s) s.remove(); };
  }, []);

  if (!isWeb) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', width: '150%', height: '200%',
        background: [
          `radial-gradient(ellipse 600px 400px at 20% 10%, ${theme.primary}08 0%, transparent 50%)`,
          `radial-gradient(ellipse 500px 350px at 80% 25%, ${theme.primaryLight}06 0%, transparent 50%)`,
          `radial-gradient(ellipse 700px 500px at 10% 50%, ${theme.primary}04 0%, transparent 50%)`,
        ].join(', '),
        animation: 'gentleFlow 30s ease-in-out infinite',
      }} />
      <svg style={{ position: 'absolute', top: '8%', left: '-5%', width: '110%', height: '150px', opacity: 0.03 }}>
        <path d="M0,20 Q200,60 400,20 T800,20 T1200,20 T1600,20" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,50 Q200,90 400,50 T800,50 T1200,50 T1600,50" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,80 Q200,120 400,80 T800,80 T1200,80 T1600,80" stroke={theme.primary} fill="none" strokeWidth="1.5" />
      </svg>
    </div>
  );
};

// Combo Input - Dropdown with text input ability
const ComboInput = ({ value, onChange, suggestions, placeholder, theme }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);

  const handleTextChange = (text) => {
    onChange(text);
    if (text) {
      setFilteredSuggestions(suggestions.filter(s => s.toLowerCase().includes(text.toLowerCase())));
    } else {
      setFilteredSuggestions(suggestions);
    }
  };

  return (
    <View style={styles.comboContainer}>
      <View style={[styles.comboInputRow, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}>
        <TextInput
          style={[styles.comboTextInput, { color: theme.textPrimary }]}
          value={value}
          onChangeText={handleTextChange}
          placeholder={placeholder}
          placeholderTextColor={theme.textMuted}
          onFocus={() => setShowDropdown(true)}
        />
        <Pressable onPress={() => setShowDropdown(!showDropdown)} style={styles.comboArrow}>
          <Text style={{ color: theme.textMuted, fontSize: 12 }}>{showDropdown ? '▲' : '▼'}</Text>
        </Pressable>
      </View>
      {showDropdown && filteredSuggestions.length > 0 && (
        <View style={[styles.comboDropdown, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <ScrollView style={{ maxHeight: 150 }} nestedScrollEnabled>
            {filteredSuggestions.map((item, idx) => (
    <Pressable 
                key={idx} 
                style={[styles.comboOption, { borderBottomColor: theme.borderLight }]}
                onPress={() => { onChange(item); setShowDropdown(false); }}
              >
                <Text style={[styles.comboOptionText, { color: theme.textPrimary }]}>{item}</Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
        </View>
  );
};

// Folder/Collection selector
const FolderSelector = ({ folders, selectedFolder, onSelect, onCreateNew, theme }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = () => {
    if (newFolderName.trim()) {
      onCreateNew(newFolderName.trim());
      setNewFolderName('');
      setIsCreating(false);
      setShowDropdown(false);
    }
  };

  return (
    <View style={styles.comboContainer}>
      <Pressable 
        style={[styles.folderSelector, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}
        onPress={() => setShowDropdown(!showDropdown)}
      >
        <View style={[styles.folderIcon, { borderColor: theme.textMuted }]} />
        <Text style={[styles.folderText, { color: selectedFolder ? theme.textPrimary : theme.textMuted }]}>
          {selectedFolder || 'No folder (root)'}
        </Text>
        <Text style={{ color: theme.textMuted, fontSize: 12 }}>{showDropdown ? '▲' : '▼'}</Text>
      </Pressable>
      {showDropdown && (
        <View style={[styles.comboDropdown, { backgroundColor: theme.surface, borderColor: theme.border }]}>
          <Pressable 
            style={[styles.comboOption, { borderBottomColor: theme.borderLight }]}
            onPress={() => { onSelect(null); setShowDropdown(false); }}
          >
            <Text style={[styles.comboOptionText, { color: theme.textMuted }]}>No folder (root)</Text>
          </Pressable>
          {folders.map((folder, idx) => (
            <Pressable 
              key={idx} 
              style={[styles.comboOption, { borderBottomColor: theme.borderLight }]}
              onPress={() => { onSelect(folder); setShowDropdown(false); }}
            >
              <Text style={[styles.comboOptionText, { color: theme.textPrimary }]}>{folder}</Text>
            </Pressable>
          ))}
          {isCreating ? (
            <View style={[styles.newFolderRow, { borderTopColor: theme.border }]}>
              <TextInput
                style={[styles.newFolderInput, { backgroundColor: theme.backgroundAlt, color: theme.textPrimary, borderColor: theme.border }]}
                value={newFolderName}
                onChangeText={setNewFolderName}
                placeholder="Folder name..."
                placeholderTextColor={theme.textMuted}
                autoFocus
              />
              <Pressable style={[styles.newFolderBtn, { backgroundColor: theme.primary }]} onPress={handleCreate}>
                <Text style={{ color: theme.textOnPrimary, fontWeight: '600' }}>Add</Text>
              </Pressable>
            </View>
          ) : (
            <Pressable 
              style={[styles.comboOption, { borderBottomColor: theme.borderLight }]}
              onPress={() => setIsCreating(true)}
            >
              <Text style={[styles.comboOptionText, { color: theme.primary }]}>+ Create new folder</Text>
            </Pressable>
          )}
        </View>
      )}
      </View>
  );
};

// Animated button with hover
const AnimatedButton = ({ children, onPress, style, disabled }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
      <Pressable 
      onPress={onPress}
      disabled={disabled}
      onPressIn={() => Animated.spring(scale, { toValue: 0.96, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onHoverIn={() => !disabled && Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[style, { transform: [{ scale }] }]}>{children}</Animated.View>
    </Pressable>
  );
};

// Floating Navbar with full options
const FloatingNavbar = ({ navigation, isDark, toggleDarkMode, user, onSignOut, screenWidth, onShowSettings, unreadCount, onBell }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  return (
    <View style={[styles.navbarContainer, { paddingHorizontal: isMobile ? 16 : 48 }]}>
      <View style={[styles.navbar, { 
        backgroundColor: theme.surfaceFrosted,
        ...Platform.select({ web: { backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: baseTheme.shadows.navbar } })
      }]}>
        <Pressable onPress={() => navigation.navigate('Landing')} style={styles.logoContainer}>
          <View style={[styles.logoIcon, { backgroundColor: theme.primary }]}>
            <View style={[styles.logoNote, { backgroundColor: theme.textOnPrimary }]} />
            <View style={[styles.logoNote2, { backgroundColor: theme.textOnPrimary }]} />
        </View>
          {!isMobile && <Text style={[styles.logoText, { color: theme.textPrimary }]}>SheetShed</Text>}
      </Pressable>

        {/* Nav Links */}
        {!isMobile && (
          <View style={styles.navLinks}>
            <Pressable style={styles.navLink} onPress={() => navigation.navigate('Landing')}>
              <Text style={[styles.navLinkText, { color: theme.textSecondary }]}>Home</Text>
    </Pressable>
            <Pressable style={[styles.navLink, styles.navLinkActive, { backgroundColor: theme.primaryLight + '20' }]}>
              <Text style={[styles.navLinkText, { color: theme.primary }]}>Library</Text>
            </Pressable>
            <Pressable style={styles.navLink} onPress={() => navigation.navigate('Plans')}>
              <Text style={[styles.navLinkText, { color: theme.textSecondary }]}>Plans</Text>
            </Pressable>
          </View>
        )}

        <View style={styles.navRight}>
          {/* Notification Bell */}
          <Pressable style={[styles.darkModeToggle, { backgroundColor: theme.surfaceHover, position: 'relative' }]} onPress={onBell}>
            <BellIcon color={unreadCount > 0 ? theme.primary : theme.textSecondary} size={20} />
            {unreadCount > 0 && (
              <View style={[styles.bellBadge, { backgroundColor: theme.primary }]}>
                <Text style={styles.bellBadgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
              </View>
            )}
          </Pressable>

          <Pressable style={[styles.darkModeToggle, { backgroundColor: theme.surfaceHover }]} onPress={toggleDarkMode}>
            {isDark ? <SunIcon color={theme.textSecondary} size={20} /> : <MoonIcon color={theme.textSecondary} size={20} />}
          </Pressable>
          
          {/* User Avatar with Menu */}
          <View style={styles.userMenuContainer}>
            <Pressable 
              style={[styles.userAvatar, { backgroundColor: theme.primary }]} 
              onPress={() => setShowUserMenu(!showUserMenu)}
            >
              <Text style={[styles.userAvatarText, { color: theme.textOnPrimary }]}>
                {user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
              </Text>
            </Pressable>
            
            {showUserMenu && (
              <View style={[styles.userMenu, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                <View style={[styles.userMenuHeader, { borderBottomColor: theme.border }]}>
                  <Text style={[styles.userMenuName, { color: theme.textPrimary }]}>{user?.displayName || 'User'}</Text>
                  <Text style={[styles.userMenuEmail, { color: theme.textMuted }]}>{user?.email}</Text>
                  {/* Plan Badge - Always show current plan */}
                  <View style={[styles.planBadge, { backgroundColor: user?.isPro ? theme.primary : theme.backgroundAlt }]}>
                    <Text style={{ color: user?.isPro ? theme.textOnPrimary : theme.textSecondary, fontSize: 11, fontWeight: '700' }}>
                      {user?.isPro ? 'PRO' : 'FREE'}
                    </Text>
                  </View>
                </View>
                <Pressable style={styles.userMenuItem} onPress={() => { setShowUserMenu(false); onShowSettings?.(); }}>
                  <Text style={[styles.userMenuItemText, { color: theme.textPrimary }]}>Settings</Text>
                </Pressable>
                {!user?.isPro && (
                  <Pressable style={styles.userMenuItem} onPress={() => { setShowUserMenu(false); navigation.navigate('Plans'); }}>
                    <Text style={[styles.userMenuItemText, { color: theme.primary }]}>Upgrade to Pro</Text>
                  </Pressable>
                )}
                <View style={[styles.userMenuDivider, { backgroundColor: theme.border }]} />
                <Pressable style={styles.userMenuItem} onPress={() => { setShowUserMenu(false); onSignOut(); }}>
                  <Text style={[styles.userMenuItemText, { color: theme.error }]}>Sign Out</Text>
                </Pressable>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

// PDF Icon component for PDF files
const PDFIcon = ({ color = '#000', size = 40 }) => {
  if (Platform.OS !== 'web') return <Text style={{ color, fontSize: size }}>PDF</Text>;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14,2 14,8 20,8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10,9 9,9 8,9"/>
    </svg>
  );
};

// Sheet Card - Clean design with ACTUAL file preview
const SheetCard = ({ sheet, onPress, onFavorite, onShare, onDelete, onShareWithUser, onMove, onPrint, onLongPress, theme, isMobile, onDragStart, cardWidth, isXL, readOnly }) => {
  const scale = useRef(new Animated.Value(1)).current;
  const [showMenu, setShowMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Determine if the file is an image
  const isImage = sheet.fileType?.startsWith('image/') || 
                  sheet.fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = sheet.fileType === 'application/pdf' || 
                sheet.fileName?.match(/\.pdf$/i);

  const cardContent = (
    <Pressable 
      onPress={() => onPress(sheet)}
      onLongPress={() => onLongPress?.(sheet)}
      delayLongPress={400}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[styles.sheetCard, { 
        backgroundColor: theme.surface, 
        transform: [{ scale }],
        width: isMobile ? '100%' : (cardWidth || 280),
      }]}>
        {/* Thumbnail - Show actual file preview */}
        <View style={[styles.sheetThumbnail, { 
          backgroundColor: theme.backgroundAlt, overflow: 'hidden',
          height: cardWidth ? Math.round(cardWidth * 0.78) : 220,
        }]}>
          {/* For images, show the actual image with full proportions */}
          {isImage && sheet.fileUrl && !imageError ? (
            isWeb ? (
              <img 
                src={sheet.fileUrl} 
                alt={sheet.title}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              <View style={styles.imagePlaceholder}>
                <MusicNoteIcon color={theme.primary} size={32} />
              </View>
            )
          ) : isPDF ? (
            // For PDFs, show a PDF indicator with file info
            <View style={styles.pdfPreview}>
              <PDFIcon color={theme.primary} size={36} />
              <Text style={[styles.pdfLabel, { color: theme.textMuted }]}>PDF</Text>
              {sheet.pageCount > 1 && (
                <Text style={[styles.pageCount, { color: theme.textMuted }]}>{sheet.pageCount} pages</Text>
              )}
            </View>
          ) : (
            // Fallback: show music note icon
            <View style={styles.fallbackPreview}>
              <MusicNoteIcon color={theme.primary} size={36} />
            </View>
          )}
          
          {/* Share indicator */}
          {sheet.isPublic && (
            <View style={[styles.shareIndicator, { backgroundColor: theme.success }]}>
              <ShareIcon color="#fff" size={12} />
            </View>
          )}
          
          {/* Favorite indicator overlay */}
          {sheet.isFavorite && (
            <View style={[styles.favoriteIndicator, { backgroundColor: theme.primary }]}>
              <StarIcon color="#fff" size={12} filled />
            </View>
          )}
      </View>

      {/* Info */}
      <View style={[styles.sheetInfo, isXL && { padding: 24 }]}>
          <Text style={[styles.sheetTitle, { color: theme.textPrimary, fontSize: isXL ? 24 : 19 }]} numberOfLines={1}>{sheet.title}</Text>
          {sheet.composer && <Text style={[styles.sheetComposer, { color: theme.textSecondary, fontSize: isXL ? 18 : 16 }]} numberOfLines={1}>{sheet.composer}</Text>}
        <View style={styles.sheetMeta}>
            {sheet.instrument && (
              <View style={[styles.metaTag, { backgroundColor: theme.backgroundAlt }]}>
                <Text style={[styles.metaTagText, { color: theme.textMuted, fontSize: isXL ? 16 : 14 }]}>{sheet.instrument}</Text>
            </View>
            )}
        </View>
      </View>

        {/* Action Buttons */}
        <View style={styles.sheetActions}>
          <Pressable
            style={[styles.actionBtn, { backgroundColor: sheet.isFavorite ? theme.primary + '22' : 'rgba(0,0,0,0.35)' }]}
            onPress={(e) => { e.stopPropagation(); onFavorite(sheet); }}
          >
            <StarIcon color={sheet.isFavorite ? theme.primary : '#fff'} size={32} filled={sheet.isFavorite} />
          </Pressable>
          <Pressable
            style={[styles.actionBtn, { backgroundColor: 'rgba(0,0,0,0.35)' }]}
            onPress={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
          >
            <MoreIcon color="#fff" size={32} />
          </Pressable>
        </View>

        {/* Action Menu */}
        {showMenu && (
          <View style={[styles.sheetMenu, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            {!readOnly && (
              <>
                <Pressable
                  style={styles.sheetMenuItem}
                  onPress={(e) => { e.stopPropagation(); setShowMenu(false); onShare?.(sheet); }}
                >
                  <Text style={[styles.sheetMenuText, { color: theme.textPrimary }]}>
                    {sheet.isPublic ? 'Copy Link' : 'Share link'}
                  </Text>
                </Pressable>
                <Pressable
                  style={styles.sheetMenuItem}
                  onPress={(e) => { e.stopPropagation(); setShowMenu(false); onShareWithUser?.(sheet); }}
                >
                  <Text style={[styles.sheetMenuText, { color: theme.textPrimary }]}>Share with user</Text>
                </Pressable>
                <Pressable
                  style={styles.sheetMenuItem}
                  onPress={(e) => { e.stopPropagation(); setShowMenu(false); onMove?.(sheet); }}
                >
                  <Text style={[styles.sheetMenuText, { color: theme.textPrimary }]}>Move to folder</Text>
                </Pressable>
                {isWeb && (
                  <Pressable
                    style={styles.sheetMenuItem}
                    onPress={(e) => { e.stopPropagation(); setShowMenu(false); onPrint?.(sheet); }}
                  >
                    <Text style={[styles.sheetMenuText, { color: theme.textPrimary }]}>Print</Text>
                  </Pressable>
                )}
                <View style={[styles.sheetMenuDivider, { backgroundColor: theme.border }]} />
                <Pressable
                  style={styles.sheetMenuItem}
                  onPress={(e) => { e.stopPropagation(); setShowMenu(false); onDelete?.(sheet); }}
                >
                  <Text style={[styles.sheetMenuText, { color: theme.error }]}>Delete</Text>
                </Pressable>
              </>
            )}
            {readOnly && (
              <View style={styles.sheetMenuItem}>
                <Text style={[styles.sheetMenuText, { color: theme.textMuted }]}>Shared with you (view only)</Text>
              </View>
            )}
          </View>
        )}
      </Animated.View>
    </Pressable>
  );

  if (!isWeb || readOnly) return cardContent;

  // On web, wrap in a native div so HTML5 drag-and-drop works reliably
  // (React Native's Pressable responder system intercepts mousedown and prevents drag init)
  return (
    <div
      draggable="true"
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', sheet.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart?.(sheet.id);
      }}
      onDragEnd={() => onDragStart?.(null)}
      style={{ cursor: 'grab', userSelect: 'none' }}
    >
      {cardContent}
    </div>
  );
};

// YouTube / link embed card
const LinkCard = ({ sheet, onPress, onFavorite, onDelete, onMove, onLongPress, theme, isMobile, cardWidth }) => {
  const scale = useRef(new Animated.Value(1)).current;
  const [showMenu, setShowMenu] = useState(false);
  const isYouTube = sheet.platform === 'youtube' && sheet.embedId;

  return (
    <Pressable
      onPress={() => onPress(sheet)}
      onLongPress={() => onLongPress?.(sheet)}
      delayLongPress={400}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[styles.sheetCard, {
        backgroundColor: theme.surface,
        transform: [{ scale }],
        width: isMobile ? '100%' : (cardWidth || 280),
      }]}>
        {/* Thumbnail */}
        <View style={[styles.sheetThumbnail, {
          backgroundColor: '#111',
          overflow: 'hidden',
          height: cardWidth ? Math.round(cardWidth * 0.78) : 220,
          position: 'relative',
        }]}>
          {isYouTube && isWeb ? (
            <img
              src={sheet.thumbnail}
              alt={sheet.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a2e' }}>
              {isWeb ? (
                <svg width={40} height={40} viewBox="0 0 24 24" fill="#FF0000">
                  <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/>
                </svg>
              ) : (
                <Text style={{ fontSize: 32 }}>▶</Text>
              )}
            </View>
          )}
          {/* Play overlay badge */}
          {isYouTube && isWeb && (
            <View style={{ position: 'absolute', bottom: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.75)', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
              {isWeb ? (
                <svg width={12} height={12} viewBox="0 0 24 24" fill="#FF0000">
                  <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/>
                </svg>
              ) : null}
              <Text style={{ color: '#fff', fontSize: 11, fontWeight: '600' }}>YouTube</Text>
            </View>
          )}
          {sheet.isFavorite && (
            <View style={[styles.favoriteIndicator, { backgroundColor: theme.primary }]}>
              <StarIcon color="#fff" size={12} filled />
            </View>
          )}
        </View>

        {/* Info */}
        <View style={styles.sheetInfo}>
          <Text style={[styles.sheetTitle, { color: theme.textPrimary }]} numberOfLines={1}>{sheet.title}</Text>
          {sheet.notes ? (
            <Text style={[styles.sheetComposer, { color: theme.textSecondary }]} numberOfLines={1}>{sheet.notes}</Text>
          ) : (
            <Text style={[styles.sheetComposer, { color: theme.textMuted, fontStyle: 'italic' }]} numberOfLines={1}>
              {sheet.platform === 'youtube' ? 'YouTube' : 'Link'}
            </Text>
          )}
        </View>

        {/* Actions */}
        <View style={styles.sheetActions}>
          <Pressable
            style={[styles.actionBtn, { backgroundColor: sheet.isFavorite ? theme.primary + '22' : 'rgba(0,0,0,0.35)' }]}
            onPress={(e) => { e.stopPropagation(); onFavorite(sheet); }}
          >
            <StarIcon color={sheet.isFavorite ? theme.primary : '#fff'} size={32} filled={sheet.isFavorite} />
          </Pressable>
          <Pressable
            style={[styles.actionBtn, { backgroundColor: 'rgba(0,0,0,0.35)' }]}
            onPress={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
          >
            <MoreIcon color="#fff" size={32} />
          </Pressable>
        </View>

        {showMenu && (
          <View style={[styles.sheetMenu, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Pressable
              style={styles.sheetMenuItem}
              onPress={(e) => { e.stopPropagation(); setShowMenu(false); onMove?.(sheet); }}
            >
              <Text style={[styles.sheetMenuText, { color: theme.textPrimary }]}>Move to folder</Text>
            </Pressable>
            <View style={[styles.sheetMenuDivider, { backgroundColor: theme.border }]} />
            <Pressable
              style={styles.sheetMenuItem}
              onPress={(e) => { e.stopPropagation(); setShowMenu(false); onDelete?.(sheet); }}
            >
              <Text style={[styles.sheetMenuText, { color: theme.error }]}>Delete</Text>
            </Pressable>
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
};

// Upload Drop Zone
// Two-panel add zone: Upload (left) + YouTube/Link (right)
const AddZone = ({ isDragging, theme, isMobile, onUpload, onAddLink, canUpload, remainingUploads, isPro, onUpgradePlan }) => {
  const atLimit = !isPro && remainingUploads <= 0;
  const uploadHover = useRef(new Animated.Value(0)).current;
  const linkHover = useRef(new Animated.Value(0)).current;

  const panelBase = {
    flex: 1,
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 20,
    paddingVertical: isMobile ? 28 : 36,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: isMobile ? 160 : 200,
  };

  return (
    <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 14, marginBottom: 28 }}>

      {/* ── Upload panel ── */}
      {atLimit ? (
        <View style={[panelBase, { borderColor: theme.error + '60', backgroundColor: theme.surface, flex: 1 }]}>
          <View style={[styles.uploadIconContainer, { backgroundColor: theme.error + '18', marginBottom: 10 }]}>
            <UploadIcon color={theme.error} size={22} />
          </View>
          <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: isMobile ? 16 : 18, textAlign: 'center', marginBottom: 6 }}>
            Limit reached
          </Text>
          <Text style={{ color: theme.textSecondary, fontSize: 13, textAlign: 'center', marginBottom: 12, maxWidth: 220 }}>
            {FREE_TIER.maxSheets} free slots used. Delete a sheet or upgrade.
          </Text>
          <Pressable onPress={onUpgradePlan} style={[styles.limitActionBtn, { backgroundColor: theme.primary }]}>
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>Upgrade</Text>
          </Pressable>
        </View>
      ) : (
        <Pressable
          style={{ flex: 1 }}
          onPress={onUpload}
          onHoverIn={() => Animated.spring(uploadHover, { toValue: 1, useNativeDriver: false }).start()}
          onHoverOut={() => Animated.spring(uploadHover, { toValue: 0, useNativeDriver: false }).start()}
        >
          <Animated.View style={[panelBase, {
            backgroundColor: isDragging
              ? `${theme.primary}12`
              : uploadHover.interpolate({ inputRange: [0, 1], outputRange: [theme.surface, `${theme.primary}08`] }),
            borderColor: isDragging ? theme.primary : uploadHover.interpolate({ inputRange: [0, 1], outputRange: [theme.border, theme.primary] }),
          }]}>
            <View style={[styles.uploadIconContainer, { backgroundColor: theme.primary, marginBottom: 10 }]}>
              <UploadIcon color={theme.textOnPrimary} size={24} />
            </View>
            <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: isMobile ? 18 : 22, textAlign: 'center', marginBottom: 4 }}>
              {isDragging ? 'Drop it here!' : 'Add Sheet Music'}
            </Text>
            <Text style={{ color: theme.textSecondary, fontSize: isMobile ? 13 : 15, textAlign: 'center', marginBottom: 6 }}>
              {isDragging ? 'Release to upload' : 'Tap to choose a file'}
            </Text>
            <Text style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', marginBottom: !isPro ? 10 : 0 }}>
              PDF, PNG, or JPG
            </Text>
            {!isPro && (
              <View style={[styles.uploadLimit, {
                backgroundColor: remainingUploads <= 3 ? theme.error + '15' : theme.backgroundAlt,
              }]}>
                <Text style={[styles.uploadLimitText, { color: remainingUploads <= 3 ? theme.error : theme.textSecondary }]}>
                  {remainingUploads} of {FREE_TIER.maxSheets} slots left
                </Text>
              </View>
            )}
          </Animated.View>
        </Pressable>
      )}

      {/* ── YouTube / Link panel ── */}
      <Pressable
        style={{ flex: 1 }}
        onPress={onAddLink}
        onHoverIn={() => Animated.spring(linkHover, { toValue: 1, useNativeDriver: false }).start()}
        onHoverOut={() => Animated.spring(linkHover, { toValue: 0, useNativeDriver: false }).start()}
      >
        <Animated.View style={[panelBase, {
          backgroundColor: linkHover.interpolate({ inputRange: [0, 1], outputRange: [theme.surface, '#FF000008'] }),
          borderColor: linkHover.interpolate({ inputRange: [0, 1], outputRange: [theme.border, '#FF0000'] }),
        }]}>
          {/* YouTube logo */}
          <View style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: '#FF0000', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            {isWeb ? (
              <svg width={28} height={28} viewBox="0 0 24 24" fill="#fff">
                <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/>
              </svg>
            ) : (
              <Text style={{ color: '#fff', fontSize: 20 }}>▶</Text>
            )}
          </View>
          <Text style={{ color: theme.textPrimary, fontWeight: '700', fontSize: isMobile ? 18 : 22, textAlign: 'center', marginBottom: 4 }}>
            Add YouTube Video
          </Text>
          <Text style={{ color: theme.textSecondary, fontSize: isMobile ? 13 : 15, textAlign: 'center', marginBottom: 6 }}>
            Tap to paste a YouTube link
          </Text>
          <Text style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center' }}>
            Works with any YouTube URL
          </Text>
        </Animated.View>
      </Pressable>

    </View>
  );
};

// Keep old name as alias so drag-overlay still works
const DropZone = AddZone;

// Section navigation pills
const SectionPill = ({ label, active, onPress, theme }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.05, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[styles.sectionPill, { 
        backgroundColor: active ? theme.primary : theme.surface,
        transform: [{ scale }],
      }]}>
        <Text style={[styles.sectionPillText, { color: active ? theme.textOnPrimary : theme.textSecondary }]}>{label}</Text>
      </Animated.View>
    </Pressable>
  );
};

// Simple QR Code component (web-based SVG)
const QRCode = ({ value, size = 180, theme }) => {
  // Generate a simple QR-like visual (for demo - in production use a library like 'qrcode')
  // This creates a visual placeholder - you'd want to use a real QR library
  const generateQRPattern = () => {
    const modules = 21; // Standard QR size
    const cellSize = size / modules;
    const cells = [];
    
    // Create pseudo-random pattern based on value hash
    const hash = value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    
    for (let row = 0; row < modules; row++) {
      for (let col = 0; col < modules; col++) {
        // Position detection patterns (corners)
        const isCorner = (row < 7 && col < 7) || 
                        (row < 7 && col >= modules - 7) || 
                        (row >= modules - 7 && col < 7);
        
        // Timing patterns
        const isTiming = (row === 6 || col === 6);
        
        // Data area - pseudo-random based on position and hash
        const isData = ((row + col + hash) % 3 === 0) || ((row * col + hash) % 5 === 0);
        
        if (isCorner || isTiming || isData) {
          cells.push(
            <rect
              key={`${row}-${col}`}
              x={col * cellSize}
              y={row * cellSize}
              width={cellSize - 0.5}
              height={cellSize - 0.5}
              fill={theme.textPrimary}
              rx={1}
            />
          );
        }
      }
    }
    return cells;
  };

  if (!isWeb) {
    return (
      <View style={{ width: size, height: size, backgroundColor: theme.backgroundAlt, borderRadius: 12, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: theme.textMuted }}>QR Code</Text>
      </View>
    );
  }

  return (
    <svg width={size} height={size} style={{ backgroundColor: 'white', borderRadius: 12, padding: 8 }}>
      {generateQRPattern()}
    </svg>
  );
};

// Share Modal Component
const ShareModal = ({ visible, sheet, shareLink, onClose, onCopy, theme, isMobile }) => {
  if (!visible || !sheet) return null;

  return (
    <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={onClose}>
      <Pressable style={[styles.shareModal, { backgroundColor: theme.surface, maxWidth: isMobile ? '95%' : 420 }]} onPress={(e) => e.stopPropagation()}>
        <View style={styles.shareModalHeader}>
          <Text style={[styles.shareModalTitle, { color: theme.textPrimary }]}>Share "{sheet.title}"</Text>
          <Pressable onPress={onClose} style={styles.modalClose}>
            <CloseIcon color={theme.textMuted} size={22} />
          </Pressable>
        </View>

        {/* QR Code */}
        <View style={styles.qrContainer}>
          <QRCode value={shareLink || 'https://sheetshed.app'} size={180} theme={theme} />
          <Text style={[styles.qrHint, { color: theme.textMuted }]}>Scan to view on another device</Text>
        </View>

        {/* Share Link */}
        <View style={[styles.shareLinkContainer, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}>
          <Text style={[styles.shareLinkText, { color: theme.textPrimary }]} numberOfLines={1}>
            {shareLink || 'Generating link...'}
          </Text>
        </View>

        {/* Actions */}
        <View style={styles.shareActions}>
          <Pressable 
            style={[styles.shareActionBtn, { backgroundColor: theme.primary }]} 
            onPress={onCopy}
          >
            <Text style={[styles.shareActionText, { color: theme.textOnPrimary }]}>Copy Link</Text>
          </Pressable>
          <Pressable 
            style={[styles.shareActionBtn, { backgroundColor: theme.backgroundAlt, borderWidth: 1, borderColor: theme.border }]} 
            onPress={() => {
              if (isWeb && navigator.share) {
                navigator.share({ title: sheet.title, url: shareLink });
              }
            }}
          >
            <Text style={[styles.shareActionText, { color: theme.textPrimary }]}>Share</Text>
          </Pressable>
        </View>

        <Text style={[styles.shareNote, { color: theme.textMuted }]}>
          Anyone with this link can view this sheet (no account required)
        </Text>
      </Pressable>
    </Pressable>
  );
};

// ─── User Share Modal ─────────────────────────────────────────────────────────
const UserShareModal = ({ visible, target, onClose, theme, isMobile }) => {
  const { searchUsers, sendShareInvite, mySharedItems, revokeShare } = useSharing();
  const [emailQuery, setEmailQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Existing shares for this resource
  const existingShares = target
    ? mySharedItems.filter(s => s.resourceId === target.id)
    : [];

  const handleSearch = async () => {
    if (!emailQuery.trim()) return;
    setSearching(true);
    setSearchError('');
    setSearchResult(null);
    setSent(false);
    const results = await searchUsers(emailQuery.trim());
    if (results.length === 0) setSearchError('No user found with that email');
    else setSearchResult(results[0]);
    setSearching(false);
  };

  const handleSend = async () => {
    if (!searchResult || !target) return;
    setSending(true);
    try {
      await sendShareInvite({
        type: target.type,
        resourceId: target.id,
        resourceTitle: target.title,
        toUserId: searchResult.id,
        toUserEmail: searchResult.email,
        toUserName: searchResult.displayName || searchResult.email,
      });
      setSent(true);
      setSearchResult(null);
      setEmailQuery('');
    } catch (err) {
      setSearchError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!visible || !target) return null;

  return (
    <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={onClose}>
      <Pressable style={[styles.modal, { backgroundColor: theme.surface, maxWidth: isMobile ? '95%' : 440 }]} onPress={(e) => e.stopPropagation()}>
        <View style={styles.modalHeader}>
          <View>
            <Text style={[styles.modalTitle, { color: theme.textPrimary }]}>Share with user</Text>
            <Text style={[styles.shareTargetLabel, { color: theme.textMuted }]}>
              {target.type === 'folder' ? 'Folder' : 'Sheet'}: {target.title}
            </Text>
          </View>
          <Pressable onPress={onClose} style={styles.modalClose}>
            <CloseIcon color={theme.textMuted} size={22} />
          </Pressable>
        </View>

        {/* Email search */}
        <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Find user by email</Text>
        <View style={[styles.shareSearchRow, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}>
          <TextInput
            style={[styles.shareSearchInput, { color: theme.textPrimary }]}
            value={emailQuery}
            onChangeText={(t) => { setEmailQuery(t); setSearchError(''); setSearchResult(null); setSent(false); }}
            placeholder="user@example.com"
            placeholderTextColor={theme.textMuted}
            keyboardType="email-address"
            autoCapitalize="none"
            onSubmitEditing={handleSearch}
          />
          <Pressable
            style={[styles.shareSearchBtn, { backgroundColor: theme.primary, opacity: searching ? 0.7 : 1 }]}
            onPress={handleSearch}
            disabled={searching}
          >
            {searching ? <ActivityIndicator size="small" color="#fff" /> : <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Find</Text>}
          </Pressable>
        </View>

        {searchError ? <Text style={[styles.shareError, { color: theme.error }]}>{searchError}</Text> : null}

        {/* Search result */}
        {searchResult && !sent && (
          <View style={[styles.userResultRow, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}>
            <View style={[styles.userResultAvatar, { backgroundColor: theme.primary }]}>
              <Text style={{ color: '#fff', fontWeight: '700' }}>
                {(searchResult.displayName || searchResult.email || '?').charAt(0).toUpperCase()}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[{ color: theme.textPrimary, fontWeight: '600', fontSize: 15 }]}>{searchResult.displayName || '(no name)'}</Text>
              <Text style={[{ color: theme.textMuted, fontSize: 13 }]}>{searchResult.email}</Text>
            </View>
            <Pressable
              style={[styles.sendInviteBtn, { backgroundColor: theme.primary, opacity: sending ? 0.7 : 1 }]}
              onPress={handleSend}
              disabled={sending}
            >
              {sending ? <ActivityIndicator size="small" color="#fff" /> : <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Invite</Text>}
            </Pressable>
          </View>
        )}

        {sent && (
          <View style={[styles.sentConfirm, { backgroundColor: '#22C55E18', borderColor: '#22C55E' }]}>
            <Text style={{ color: '#22C55E', fontWeight: '600' }}>Invite sent! They'll see it in their notifications.</Text>
          </View>
        )}

        {/* Existing shares */}
        {existingShares.length > 0 && (
          <View style={{ marginTop: 20 }}>
            <Text style={[styles.modalLabel, { color: theme.textSecondary, marginBottom: 10 }]}>Currently shared with</Text>
            {existingShares.map(share => (
              <View key={share.id} style={[styles.existingShareRow, { borderBottomColor: theme.border }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[{ color: theme.textPrimary, fontSize: 14, fontWeight: '500' }]}>{share.sharedWithEmail}</Text>
                  <Text style={[{ color: theme.textMuted, fontSize: 12 }]}>
                    {share.status === 'pending' ? 'Pending acceptance' : share.status === 'accepted' ? 'Has access' : 'Declined'}
                  </Text>
                </View>
                <Pressable
                  style={[styles.revokeBtn, { borderColor: theme.error }]}
                  onPress={() => revokeShare(share.id)}
                >
                  <Text style={[{ color: theme.error, fontSize: 13, fontWeight: '500' }]}>Revoke</Text>
                </Pressable>
              </View>
            ))}
          </View>
        )}
      </Pressable>
    </Pressable>
  );
};

// ─── Notifications Panel ──────────────────────────────────────────────────────
const NotificationsPanel = ({ visible, onClose, theme, isMobile, respondToConnection }) => {
  const { notifications, unreadCount, markRead, markAllRead, deleteNotification } = useNotifications();
  const { respondToShare } = useSharing();

  if (!visible) return null;

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={onClose}>
      <Pressable
        style={[styles.notifPanel, { backgroundColor: theme.surface, maxWidth: isMobile ? '95%' : 420 }]}
        onPress={(e) => e.stopPropagation()}
      >
        <View style={styles.notifHeader}>
          <Text style={[styles.notifTitle, { color: theme.textPrimary }]}>Notifications</Text>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            {unreadCount > 0 && (
              <Pressable onPress={markAllRead} style={[styles.markAllBtn, { borderColor: theme.border }]}>
                <Text style={[{ color: theme.textSecondary, fontSize: 13 }]}>Mark all read</Text>
              </Pressable>
            )}
            <Pressable onPress={onClose} style={styles.modalClose}>
              <CloseIcon color={theme.textMuted} size={22} />
            </Pressable>
          </View>
        </View>

        <ScrollView style={{ maxHeight: isMobile ? '70vh' : 480 }} showsVerticalScrollIndicator={false}>
          {notifications.length === 0 && (
            <View style={{ padding: 40, alignItems: 'center' }}>
              <BellIcon color={theme.textMuted} size={32} />
              <Text style={[{ color: theme.textMuted, marginTop: 12, fontSize: 15 }]}>No notifications yet</Text>
            </View>
          )}
          {notifications.map(notif => {
            const isUnread = !notif.read;
            const isShareInvite = notif.type === 'share_invite';
            const isConnRequest = notif.type === 'connection_request';
            const p = notif.payload || {};

            return (
              <Pressable
                key={notif.id}
                style={[styles.notifItem, { borderBottomColor: theme.border, backgroundColor: isUnread ? theme.primary + '08' : 'transparent' }]}
                onPress={() => markRead(notif.id)}
              >
                {/* Dot indicator */}
                {isUnread && <View style={[styles.notifDot, { backgroundColor: theme.primary }]} />}

                <View style={{ flex: 1 }}>
                  {isShareInvite && (
                    <>
                      <Text style={[styles.notifText, { color: theme.textPrimary }]}>
                        <Text style={{ fontWeight: '700' }}>{p.fromUserName}</Text>
                        {' shared '}
                        <Text style={{ fontWeight: '600' }}>"{p.resourceTitle}"</Text>
                        {` (${p.resourceType || 'sheet'}) with you`}
                      </Text>
                      <View style={styles.notifActions}>
                        <Pressable
                          style={[styles.notifActionBtn, { backgroundColor: theme.primary }]}
                          onPress={async () => { await respondToShare(p.shareId, true); await markRead(notif.id); }}
                        >
                          <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Accept</Text>
                        </Pressable>
                        <Pressable
                          style={[styles.notifActionBtn, { backgroundColor: theme.backgroundAlt, borderWidth: 1, borderColor: theme.border }]}
                          onPress={async () => { await respondToShare(p.shareId, false); await markRead(notif.id); }}
                        >
                          <Text style={{ color: theme.textSecondary, fontWeight: '600', fontSize: 13 }}>Decline</Text>
                        </Pressable>
                      </View>
                    </>
                  )}
                  {isConnRequest && (
                    <>
                      <Text style={[styles.notifText, { color: theme.textPrimary }]}>
                        <Text style={{ fontWeight: '700' }}>{p.fromUserName}</Text>
                        {' wants to connect with you'}
                      </Text>
                      <View style={styles.notifActions}>
                        <Pressable
                          style={[styles.notifActionBtn, { backgroundColor: theme.primary }]}
                          onPress={async () => { await respondToConnection(p.connectionId, true); await markRead(notif.id); }}
                        >
                          <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13 }}>Accept</Text>
                        </Pressable>
                        <Pressable
                          style={[styles.notifActionBtn, { backgroundColor: theme.backgroundAlt, borderWidth: 1, borderColor: theme.border }]}
                          onPress={async () => { await respondToConnection(p.connectionId, false); await markRead(notif.id); }}
                        >
                          <Text style={{ color: theme.textSecondary, fontWeight: '600', fontSize: 13 }}>Decline</Text>
                        </Pressable>
                      </View>
                    </>
                  )}
                  {!isShareInvite && !isConnRequest && (
                    <Text style={[styles.notifText, { color: theme.textPrimary }]}>
                      {notif.type === 'share_accepted'
                        ? `${p.byUserName} accepted your share of "${p.resourceTitle}"`
                        : notif.type === 'connection_accepted'
                        ? `${p.byUserName} accepted your connection request`
                        : notif.type}
                    </Text>
                  )}
                  <Text style={[styles.notifTime, { color: theme.textMuted }]}>{formatTime(notif.createdAt)}</Text>
                </View>
                <Pressable onPress={() => deleteNotification(notif.id)} style={{ padding: 6 }}>
                  <CloseIcon color={theme.textMuted} size={14} />
                </Pressable>
              </Pressable>
            );
          })}
        </ScrollView>
      </Pressable>
    </Pressable>
  );
};

export const LibraryScreen = ({ navigation }) => {
  const { user, signOut, isPro, getRemainingUploads, checkCanUpload } = useAuth();
  const { sheets, folders, loading, uploadSheet, addLink, toggleFavorite, toggleFolderFavorite, createFolder, updateFolder, deleteFolder, moveToFolder, deleteSheet, createShareLink, extractYouTubeId } = useSheets();
  const { sharedWithMe, pendingShares } = useSharing();
  const { unreadCount } = useNotifications();
  const { respondToConnection } = useConnections();
  
  const [isDark, setIsDark] = useState(false);
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareSheet, setShareSheet] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [activeSection, setActiveSection] = useState('all');
  const [activeFolder, setActiveFolder] = useState(null); // stores folder ID
  const [uploadMetadata, setUploadMetadata] = useState({ title: '', composer: '', instrument: '', genre: '', folderId: null });

  // Folder editing state
  const [folderEditModal, setFolderEditModal] = useState(null); // { id|null, name, color }
  const [folderMenuOpen, setFolderMenuOpen] = useState(null);
  const [folderDragTarget, setFolderDragTarget] = useState(null);
  const [draggingSheetId, setDraggingSheetId] = useState(null);

  // Sharing UI state
  const [showNotifications, setShowNotifications] = useState(false);
  const [shareTarget, setShareTarget] = useState(null); // { type: 'sheet'|'folder', id, title }

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null); // sheet object
  const [isDeleting, setIsDeleting] = useState(false);

  // Move to folder modal state
  const [moveTarget, setMoveTarget] = useState(null); // sheet/link object
  const [isMoving, setIsMoving] = useState(false);

  // Add Link modal state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkNotes, setLinkNotes] = useState('');
  const [linkFolderId, setLinkFolderId] = useState(null);
  const [isAddingLink, setIsAddingLink] = useState(false);
  const [linkUrlError, setLinkUrlError] = useState('');
  const [isFetchingMeta, setIsFetchingMeta] = useState(false);
  const linkFetchTimer = useRef(null);

  // View controls — persisted to localStorage so the choice survives reloads & sessions
  const [cardScale, setCardScale] = useState(() => {
    if (isWeb && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sheetshed_cardscale');
      if (saved && ['small', 'medium', 'large', 'xl'].includes(saved)) return saved;
    }
    return 'xl';
  });
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterInstrument, setFilterInstrument] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const fileInputRef = useRef(null);
  const theme = isDark ? baseTheme.dark : baseTheme.light;

  // Close menus on outside click
  useEffect(() => {
    if (!isWeb) return;
    const close = () => {
      setFolderMenuOpen(null);
      setShowSortMenu(false);
      setShowFilterMenu(false);
    };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);
  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';

  // Load dark mode preference
  useEffect(() => {
    if (isWeb && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sheetshed_darkmode');
      if (saved === 'true') setIsDark(true);
    }
  }, []);

  const toggleDarkMode = () => {
    const newValue = !isDark;
    setIsDark(newValue);
    if (isWeb && typeof localStorage !== 'undefined') {
      localStorage.setItem('sheetshed_darkmode', String(newValue));
    }
  };

  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const CARD_SIZES = { small: 200, medium: 280 };

  // Inline the same breakpoint padding logic as getPadding() (defined later in the component)
  const _hPad = breakpoint === 'mobile' ? 20 : breakpoint === 'mobileLarge' ? 28 : breakpoint === 'tablet' ? 40 : 60;
  const containerWidth = screenWidth - _hPad * 2;

  // XL = 1 column (full container), L = 2 columns (half container), S/M = fixed px
  const cardWidth = isMobile
    ? undefined
    : cardScale === 'xl'
      ? containerWidth
      : cardScale === 'large'
        ? Math.floor((containerWidth - 24) / 2)  // 24 = gap between 2 cols
        : CARD_SIZES[cardScale] || 280;

  const SORT_OPTIONS = [
    { id: 'date_desc', label: 'Newest first' },
    { id: 'date_asc',  label: 'Oldest first' },
    { id: 'name_asc',  label: 'Name A → Z' },
    { id: 'name_desc', label: 'Name Z → A' },
    { id: 'favorites', label: 'Favorites first' },
  ];

  // For "Shared with me" section, show accepted shares as virtual cards
  const sharedCards = activeSection === 'shared'
    ? sharedWithMe.filter(s => {
        const q = searchQuery.toLowerCase();
        return !q || s.resourceTitle?.toLowerCase().includes(q);
      })
    : [];

  const baseFiltered = activeSection === 'shared' ? [] : sheets.filter(sheet => {
    const query = searchQuery.toLowerCase();
    const matchesQuery = (
      sheet.title?.toLowerCase().includes(query) ||
      sheet.composer?.toLowerCase().includes(query) ||
      sheet.instrument?.toLowerCase().includes(query)
    );
    const matchesInstrument = !filterInstrument || sheet.instrument?.toLowerCase().includes(filterInstrument.toLowerCase());
    const matchesGenre = !filterGenre || sheet.genre?.toLowerCase().includes(filterGenre.toLowerCase());

    if (activeFolder) {
      // Inside a folder: show only sheets belonging to this folder
      return sheet.folderId === activeFolder && matchesQuery && matchesInstrument && matchesGenre;
    }

    if (activeSection === 'favorites') {
      // Favorites: show favorited sheets regardless of folder
      return matchesQuery && sheet.isFavorite && matchesInstrument && matchesGenre;
    }

    // Root "All Sheets" view: only show sheets NOT in any folder (unfiled)
    return matchesQuery && !sheet.folderId && matchesInstrument && matchesGenre;
  });

  const filteredSheets = [...baseFiltered].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc':  return (a.title || '').localeCompare(b.title || '');
      case 'name_desc': return (b.title || '').localeCompare(a.title || '');
      case 'date_asc':  return new Date(a.createdAt) - new Date(b.createdAt);
      case 'favorites': return (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0);
      default:          return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  // All folders from Firestore
  const allFolders = folders || [];

  const openNewFolderModal = () => {
    setFolderEditModal({ id: null, name: '', color: FOLDER_COLORS[Math.floor(Math.random() * FOLDER_COLORS.length)] });
  };

  const handleCreateFolder = async (name, color) => {
    const exists = allFolders.some(f => f.name.toLowerCase() === name.toLowerCase());
    if (!exists && name.trim()) {
      try {
        const id = await createFolder(name.trim());
        if (color) {
          // Update color right after creation
          await updateFolder(id, { color }).catch(() => {});
        }
      } catch (e) {
        alert('Could not create folder');
      }
    }
  };

  const handleRenameFolder = async (folderId, newName) => {
    try {
      await updateFolder(folderId, { name: newName });
    } catch (e) {
      alert('Could not rename folder');
    }
  };

  const handleRecolorFolder = async (folderId, color) => {
    try {
      await updateFolder(folderId, { color });
    } catch (e) {
      alert('Could not recolor folder');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (confirm('Delete this folder? Sheets inside will be moved to root.')) {
      try {
        await deleteFolder(folderId);
        if (activeFolder === folderId) setActiveFolder(null);
      } catch (e) {
        alert('Could not delete folder');
      }
    }
  };

  const handleDropOnFolder = async (folderId) => {
    if (draggingSheetId && folderId !== null) {
      try {
        await moveToFolder(draggingSheetId, folderId);
      } catch (e) {
        alert('Could not move sheet');
      }
    }
    setDraggingSheetId(null);
    setFolderDragTarget(null);
  };

  // Handle sharing a sheet - opens share modal with QR code
  const handleShareSheet = async (sheet) => {
    setShareSheet(sheet);
    setShareLink('');
    setShowShareModal(true);
    
    try {
      const link = await createShareLink(sheet.id);
      setShareLink(link);
    } catch (e) {
      // Generate a fallback link for demo
      const fallbackLink = `${isWeb ? window.location.origin : 'https://sheetshed.app'}/shared/${sheet.id}`;
      setShareLink(fallbackLink);
    }
  };

  const handleCopyShareLink = async () => {
    if (isWeb && navigator.clipboard && shareLink) {
      await navigator.clipboard.writeText(shareLink);
      alert('Link copied to clipboard!');
    }
  };

  // Print a sheet (web only)
  const handlePrintSheet = (sheet) => {
    if (!isWeb || !sheet.fileUrl) return;
    const isPDF = sheet.fileType === 'application/pdf' || sheet.fileName?.match(/\.pdf$/i);
    const isImage = sheet.fileType?.startsWith('image/') || sheet.fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

    if (isPDF) {
      // Hidden iframe trick — triggers the browser's native PDF print dialog
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:0';
      iframe.src = sheet.fileUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (_) {
          // Cross-origin fallback: open in new tab so user can Cmd+P
          window.open(sheet.fileUrl, '_blank');
        }
        setTimeout(() => document.body.removeChild(iframe), 5000);
      };
    } else if (isImage) {
      const win = window.open('', '_blank');
      win.document.write(
        `<html><head><title>${sheet.title}</title>` +
        `<style>*{margin:0;padding:0}body{display:flex;justify-content:center;align-items:flex-start}img{max-width:100%;height:auto;page-break-inside:avoid}</style>` +
        `</head><body><img src="${sheet.fileUrl}" onload="window.print()"/></body></html>`
      );
      win.document.close();
    }
  };

  // Handle deleting a sheet
  const handleDeleteSheet = (sheet) => {
    setDeleteTarget(sheet);
  };

  const confirmDeleteSheet = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteSheet(deleteTarget);
      setDeleteTarget(null);
    } catch {
      // error handled in hook
    } finally {
      setIsDeleting(false);
    }
  };

  // Drag and drop handlers - use native DOM events for web
  useEffect(() => {
    if (!isWeb) return;
    
    const isFileDrag = (e) => {
      // Only treat as file upload drag when dragging files FROM the OS (not internal elements)
      const types = e.dataTransfer?.types ? Array.from(e.dataTransfer.types) : [];
      return types.includes('Files') && !types.includes('text/plain');
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isFileDrag(e)) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.relatedTarget === null || !document.body.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      // Only upload if it's a real file from the OS, not an internal sheet drag
      const file = e.dataTransfer?.files?.[0];
      if (file && isFileDrag(e)) {
        handleFileSelect(file);
      }
    };
    
    // Add listeners to document body for full-page drop support
    document.body.addEventListener('dragover', handleDragOver);
    document.body.addEventListener('dragleave', handleDragLeave);
    document.body.addEventListener('drop', handleDrop);
    
    return () => {
      document.body.removeEventListener('dragover', handleDragOver);
      document.body.removeEventListener('dragleave', handleDragLeave);
      document.body.removeEventListener('drop', handleDrop);
    };
  }, []);

  const handleFileSelect = (file) => {
    const validTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PDF or image file');
      return;
    }
    if (!checkCanUpload?.()) {
      alert('You\'ve reached your upload limit. Upgrade to Pro for unlimited uploads!');
      navigation.navigate('Plans');
      return;
    }
    setUploadFile(file);
    setUploadMetadata({ ...uploadMetadata, title: file.name.replace(/\.[^/.]+$/, '') });
    setShowUploadModal(true);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    try {
      await uploadSheet(uploadFile, uploadMetadata);
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadMetadata({ title: '', composer: '', instrument: '', genre: '', folderId: null });
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleMoveToFolder = async (folderId) => {
    if (!moveTarget) return;
    setIsMoving(true);
    try {
      await moveToFolder(moveTarget.id, folderId);
      setMoveTarget(null);
    } catch (err) {
      alert('Could not move item: ' + err.message);
    } finally {
      setIsMoving(false);
    }
  };

  const openAddLinkModal = () => {
    if (linkFetchTimer.current) clearTimeout(linkFetchTimer.current);
    setLinkUrl('');
    setLinkTitle('');
    setLinkNotes('');
    setLinkFolderId(activeFolder || null);
    setLinkUrlError('');
    setIsFetchingMeta(false);
    setShowLinkModal(true);
  };

  const handleLinkUrlChange = (text) => {
    setLinkUrl(text);
    setLinkUrlError('');

    // Debounce oEmbed fetch — only when there's a YouTube ID in the URL
    if (linkFetchTimer.current) clearTimeout(linkFetchTimer.current);
    const trimmed = text.trim();
    const ytId = extractYouTubeId?.(trimmed);
    if (!ytId) return;

    linkFetchTimer.current = setTimeout(async () => {
      setIsFetchingMeta(true);
      try {
        const res = await fetch(
          `https://www.youtube.com/oembed?url=${encodeURIComponent(trimmed)}&format=json`
        );
        if (res.ok) {
          const data = await res.json();
          // Only overwrite title/notes if the user hasn't typed something already
          if (data.title) setLinkTitle(prev => prev ? prev : data.title);
          if (data.author_name) setLinkNotes(prev => prev ? prev : `by ${data.author_name}`);
        }
      } catch (_) {
        // oEmbed failed silently — user can type manually
      } finally {
        setIsFetchingMeta(false);
      }
    }, 600);
  };

  const handleAddLink = async () => {
    const trimmed = linkUrl.trim();
    if (!trimmed) { setLinkUrlError('Please enter a URL'); return; }
    try { new URL(trimmed); } catch { setLinkUrlError('Please enter a valid URL'); return; }
    if (!linkTitle.trim()) { setLinkUrlError('Please enter a title'); return; }
    setIsAddingLink(true);
    try {
      await addLink({ title: linkTitle, linkUrl: trimmed, folderId: linkFolderId, notes: linkNotes });
      setShowLinkModal(false);
    } catch (err) {
      setLinkUrlError('Failed to save link: ' + err.message);
    } finally {
      setIsAddingLink(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigation.replace('Landing');
  };

  const getPadding = () => {
    if (breakpoint === 'mobile') return 20;
    if (breakpoint === 'mobileLarge') return 28;
    if (breakpoint === 'tablet') return 40;
    return 60;
  };

  const remainingUploads = getRemainingUploads?.() || FREE_TIER.maxSheets;

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <StaccatoCursor />
      <MusicalDecorations isDark={isDark} />
      
      <FloatingNavbar 
        navigation={navigation} 
        isDark={isDark} 
        toggleDarkMode={toggleDarkMode} 
        user={user} 
        onSignOut={handleSignOut}
        screenWidth={screenWidth}
        onShowSettings={() => navigation.navigate('Settings')}
        unreadCount={unreadCount}
        onBell={() => setShowNotifications(true)}
      />

      {/* Move-mode banner — shown when long-pressing a card to pick a destination folder */}
      {moveTarget && (
        <View style={[styles.moveBanner, { backgroundColor: theme.primary }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.moveBannerTitle}>Moving: {moveTarget.title}</Text>
            <Text style={styles.moveBannerSub}>Tap a folder below to move it there, or tap a folder pill above</Text>
          </View>
          <Pressable onPress={() => setMoveTarget(null)} style={styles.moveBannerCancel}>
            <CloseIcon color="#fff" size={18} />
          </Pressable>
        </View>
      )}

      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.scrollContent, { paddingHorizontal: getPadding() }]} showsVerticalScrollIndicator={false}>
        {/* Welcome Header */}
        <View style={styles.welcomeSection}>
          <Text style={[styles.welcomeText, { color: theme.textSecondary }]}>Welcome back,</Text>
          <Text style={[styles.userName, { color: theme.textPrimary, fontSize: isMobile ? 38 : 52 }]}>
            {user?.displayName?.split(' ')[0] || 'Musician'}
          </Text>
        </View>

        {/* Stats Row */}
        <View style={[styles.statsRow, { flexDirection: isMobile ? 'column' : 'row' }]}>
          <View style={[styles.statCard, { backgroundColor: theme.surface }]}>
            <Text style={[styles.statValue, { color: theme.textPrimary }]}>{sheets.length}</Text>
            <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Total Sheets</Text>
            </View>
          <View style={[styles.statCard, { backgroundColor: theme.surface }]}>
            <Text style={[styles.statValue, { color: theme.textPrimary }]}>{sheets.filter(s => s.isFavorite).length}</Text>
            <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Favorites</Text>
            </View>
          <View style={[styles.statCard, { backgroundColor: theme.primary }]}>
            <Text style={[styles.statValue, { color: theme.textOnPrimary }]}>{user?.isPro ? 'Unlimited' : remainingUploads}</Text>
            <Text style={[styles.statLabel, { color: theme.textOnPrimary, opacity: 0.8 }]}>Uploads Left</Text>
        </View>
      </View>

        {/* Add Zone — sheet upload + YouTube side by side */}
        <AddZone
          isDragging={isDragging}
          theme={theme}
          isMobile={isMobile}
          onUpload={() => fileInputRef.current?.click()}
          onAddLink={openAddLinkModal}
          canUpload={checkCanUpload?.()}
          remainingUploads={remainingUploads}
          isPro={user?.isPro}
          onUpgradePlan={() => navigation.navigate('Plans')}
        />
        {isWeb && (
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
          />
        )}

        {/* Search + Toolbar row */}
        <View style={[styles.toolbarRow, { flexWrap: isMobile ? 'wrap' : 'nowrap' }]}>
          {/* Search input */}
          <View style={[styles.toolbarSearch, { backgroundColor: theme.surface, borderColor: theme.border, flex: isMobile ? 1 : undefined, width: isMobile ? '100%' : 260 }]}>
            <SearchIcon color={theme.textMuted} size={16} />
            <TextInput
              style={[styles.toolbarSearchInput, { color: theme.textPrimary }]}
              placeholder="Search sheets..."
              placeholderTextColor={theme.textMuted}
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <Pressable onPress={() => setSearchQuery('')}>
                <CloseIcon color={theme.textMuted} size={14} />
              </Pressable>
            )}
          </View>

          {/* Right-side controls: Sort / Filter / Scale */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: isMobile ? 0 : 'auto' }}>
            {/* Sort */}
            <View style={{ position: 'relative', zIndex: showSortMenu ? 200 : 10 }} {...(isWeb ? { onClick: (e) => e.stopPropagation() } : {})}>
              <Pressable
                style={[styles.toolbarBtn, { backgroundColor: showSortMenu ? theme.primary : theme.surface, borderColor: showSortMenu ? theme.primary : theme.border }]}
                onPress={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false); }}
              >
                <SortIcon color={showSortMenu ? theme.textOnPrimary : theme.textSecondary} size={15} />
                <Text style={[styles.toolbarBtnText, { color: showSortMenu ? theme.textOnPrimary : theme.textSecondary }]}>
                  {SORT_OPTIONS.find(o => o.id === sortBy)?.label || 'Sort'}
                </Text>
              </Pressable>
              {showSortMenu && (
                <View style={[styles.toolbarDropdown, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                  {SORT_OPTIONS.map(opt => (
                    <Pressable
                      key={opt.id}
                      style={[styles.toolbarDropdownItem, sortBy === opt.id && { backgroundColor: theme.primary + '18' }]}
                      onPress={() => { setSortBy(opt.id); setShowSortMenu(false); }}
                    >
                      <Text style={[styles.toolbarDropdownText, { color: sortBy === opt.id ? theme.primary : theme.textPrimary }]}>{opt.label}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>

            {/* Filter */}
            <View style={{ position: 'relative', zIndex: showFilterMenu ? 200 : 10 }} {...(isWeb ? { onClick: (e) => e.stopPropagation() } : {})}>
              <Pressable
                style={[styles.toolbarBtn, {
                  backgroundColor: (filterInstrument || filterGenre || showFilterMenu) ? theme.primary : theme.surface,
                  borderColor: (filterInstrument || filterGenre || showFilterMenu) ? theme.primary : theme.border,
                }]}
                onPress={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false); }}
              >
                <FilterIcon color={(filterInstrument || filterGenre || showFilterMenu) ? theme.textOnPrimary : theme.textSecondary} size={15} />
                <Text style={[styles.toolbarBtnText, { color: (filterInstrument || filterGenre || showFilterMenu) ? theme.textOnPrimary : theme.textSecondary }]}>Filter</Text>
              </Pressable>
              {showFilterMenu && (
                <View style={[styles.toolbarDropdown, { backgroundColor: theme.surface, borderColor: theme.border, width: 320, padding: 16 }]}>
                  <Text style={[styles.filterLabel, { color: theme.textSecondary }]}>Instrument</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 14 }}>
                    <View style={{ flexDirection: 'row', gap: 8, paddingVertical: 4 }}>
                      {INSTRUMENTS.map((inst) => (
                        <Pressable
                          key={inst}
                          style={[styles.filterPill, {
                            backgroundColor: filterInstrument === inst ? theme.primary : theme.backgroundAlt,
                            borderColor: filterInstrument === inst ? theme.primary : theme.border,
                          }]}
                          onPress={() => setFilterInstrument(filterInstrument === inst ? '' : inst)}
                        >
                          <Text style={[styles.filterPillText, { color: filterInstrument === inst ? theme.textOnPrimary : theme.textSecondary }]}>{inst}</Text>
                        </Pressable>
                      ))}
                    </View>
                  </ScrollView>
                  <Text style={[styles.filterLabel, { color: theme.textSecondary }]}>Genre</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 8 }}>
                    <View style={{ flexDirection: 'row', gap: 8, paddingVertical: 4 }}>
                      {GENRES.map((genre) => (
                        <Pressable
                          key={genre}
                          style={[styles.filterPill, {
                            backgroundColor: filterGenre === genre ? theme.primary : theme.backgroundAlt,
                            borderColor: filterGenre === genre ? theme.primary : theme.border,
                          }]}
                          onPress={() => setFilterGenre(filterGenre === genre ? '' : genre)}
                        >
                          <Text style={[styles.filterPillText, { color: filterGenre === genre ? theme.textOnPrimary : theme.textSecondary }]}>{genre}</Text>
                        </Pressable>
                      ))}
                    </View>
                  </ScrollView>
                  {(filterInstrument || filterGenre) && (
                    <Pressable onPress={() => { setFilterInstrument(''); setFilterGenre(''); }} style={{ marginTop: 4 }}>
                      <Text style={{ color: theme.error, fontSize: 13, fontWeight: '500' }}>Clear all filters</Text>
                    </Pressable>
                  )}
                </View>
              )}
            </View>

            {/* Scale toggle */}
            <View style={[styles.scaleToggle, { backgroundColor: theme.surface, borderColor: theme.border }]}>
              {(['small', 'medium', 'large', 'xl']).map((s) => (
                <Pressable
                  key={s}
                  style={[styles.scaleBtn, cardScale === s && { backgroundColor: theme.primary }]}
                  onPress={() => {
                    setCardScale(s);
                    if (isWeb && typeof localStorage !== 'undefined') localStorage.setItem('sheetshed_cardscale', s);
                  }}
                >
                  <Text style={[styles.scaleBtnText, { color: cardScale === s ? theme.textOnPrimary : theme.textSecondary }]}>
                    {s === 'small' ? 'S' : s === 'medium' ? 'M' : s === 'large' ? 'L' : 'XL'}
                  </Text>
                </Pressable>
              ))}
            </View>
          </View>
        </View>

        {/* Section Pills */}
        <View style={[styles.sectionHeader, { flexWrap: 'wrap', gap: 12, marginTop: 8 }]}>
          <View style={[styles.sectionPills, { flexDirection: 'row', flexWrap: 'wrap' }]}>
            <SectionPill label="All Sheets" active={activeSection === 'all' && !activeFolder} onPress={() => { setActiveSection('all'); setActiveFolder(null); }} theme={theme} />
            <SectionPill label="Favorites" active={activeSection === 'favorites'} onPress={() => { setActiveSection('favorites'); setActiveFolder(null); }} theme={theme} />
            <View style={{ position: 'relative' }}>
              <SectionPill
                label="Shared with me"
                active={activeSection === 'shared'}
                onPress={() => { setActiveSection('shared'); setActiveFolder(null); }}
                theme={theme}
              />
              {pendingShares.length > 0 && (
                <View style={[styles.bellBadge, { backgroundColor: theme.primary, top: 4, right: 4 }]}>
                  <Text style={styles.bellBadgeText}>{pendingShares.length}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Active filter chips */}
        {(filterInstrument || filterGenre) && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: -12, marginBottom: 16 }}>
            {filterInstrument && (
              <View style={[styles.filterChip, { backgroundColor: theme.primary + '20', borderColor: theme.primary }]}>
                <Text style={{ color: theme.primary, fontSize: 13 }}>{filterInstrument}</Text>
                <Pressable onPress={() => setFilterInstrument('')}><CloseIcon color={theme.primary} size={12} /></Pressable>
              </View>
            )}
            {filterGenre && (
              <View style={[styles.filterChip, { backgroundColor: theme.primary + '20', borderColor: theme.primary }]}>
                <Text style={{ color: theme.primary, fontSize: 13 }}>{filterGenre}</Text>
                <Pressable onPress={() => setFilterGenre('')}><CloseIcon color={theme.primary} size={12} /></Pressable>
              </View>
            )}
          </View>
        )}

        {/* Folder Pills */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.folderPillsScroll}
          contentContainerStyle={styles.folderPillsContainer}
        >
          {allFolders.map((folder) => {
            const isActive = activeFolder === folder.id;
            const isDragOver = folderDragTarget === folder.id;
            const pillColor = folder.color || theme.primary;
            return (
              <View key={folder.id} style={{ position: 'relative' }} {...(isWeb ? { onClick: (e) => e.stopPropagation() } : {})}>
                {isWeb && (
                  <div
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setFolderDragTarget(folder.id); }}
                    onDragLeave={(e) => { e.stopPropagation(); setFolderDragTarget(null); }}
                    onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDropOnFolder(folder.id); }}
                    style={{ position: 'absolute', inset: 0, zIndex: 2, borderRadius: 999 }}
                  />
                )}
                <Pressable
                  style={[styles.folderPill, {
                    backgroundColor: moveTarget ? `${pillColor}22` : isActive ? pillColor : isDragOver ? `${pillColor}22` : theme.surface,
                    borderColor: moveTarget ? pillColor : isActive || isDragOver ? pillColor : theme.border,
                    borderWidth: moveTarget || isDragOver ? 2 : 1,
                    transform: [{ scale: moveTarget ? 1.04 : 1 }],
                  }]}
                  onPress={() => {
                    if (moveTarget) {
                      handleMoveToFolder(folder.id);
                      return;
                    }
                    setActiveFolder(isActive ? null : folder.id);
                    setActiveSection('all');
                    setFolderMenuOpen(null);
                  }}
                >
                  {/* Color dot */}
                  <View style={[styles.folderColorDot, { backgroundColor: pillColor, opacity: isActive ? 0 : 1 }]} />
                  {isActive && <FolderIcon color={theme.textOnPrimary} size={14} filled />}
                  <Text style={[styles.folderPillText, { color: isActive ? theme.textOnPrimary : theme.textPrimary }]}>
                    {folder.name}
                  </Text>
                  {isActive && (
                    <Pressable
                      onPress={(e) => { e.stopPropagation?.(); setActiveFolder(null); }}
                      style={{ marginLeft: 4 }}
                    >
                      <CloseIcon color={theme.textOnPrimary} size={12} />
                    </Pressable>
                  )}
                  {!isActive && (
                    <Pressable
                      style={styles.folderMenuBtn}
                      onPress={(e) => {
                        e.stopPropagation?.();
                        setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id);
                      }}
                    >
                      <MoreIcon color={theme.textMuted} size={14} />
                    </Pressable>
                  )}
                </Pressable>

                {/* Folder context menu */}
                {folderMenuOpen === folder.id && (
                  <View style={[styles.folderContextMenu, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                    <Pressable
                      style={styles.folderContextMenuItem}
                      onPress={() => {
                        setFolderMenuOpen(null);
                        setFolderEditModal({ id: folder.id, name: folder.name, color: folder.color || FOLDER_COLORS[0] });
                      }}
                    >
                      <Text style={[styles.folderContextMenuText, { color: theme.textPrimary }]}>Rename / Recolor</Text>
                    </Pressable>
                    <View style={[styles.sheetMenuDivider, { backgroundColor: theme.border }]} />
                    <Pressable
                      style={styles.folderContextMenuItem}
                      onPress={() => { setFolderMenuOpen(null); handleDeleteFolder(folder.id); }}
                    >
                      <Text style={[styles.folderContextMenuText, { color: theme.error }]}>Delete</Text>
                    </Pressable>
                  </View>
                )}
              </View>
            );
          })}

          {/* Add New Folder */}
          <Pressable
            style={[styles.folderPill, styles.addFolderPill, { borderColor: theme.primary }]}
            onPress={openNewFolderModal}
          >
            <PlusIcon color={theme.primary} size={14} />
            <Text style={[styles.folderPillText, { color: theme.primary }]}>New Folder</Text>
          </Pressable>
        </ScrollView>

        {/* Folder Cards */}
        {!activeFolder && (activeSection === 'all' || activeSection === 'shared' || activeSection === 'favorites') && !searchQuery && (
          <View style={[styles.sheetsGrid, { gap: isMobile ? 16 : 24, marginBottom: 8 }]}>
            {allFolders.filter(folder => activeSection !== 'favorites' || folder.isFavorite).map((folder) => {
              const pillColor = folder.color || theme.primary;
              const count = sheets.filter(s => s.folderId === folder.id).length;
              const isDragOver = folderDragTarget === folder.id;
              return (
                <View key={folder.id} style={{ position: 'relative' }}>
                  {isWeb && (
                    <div
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setFolderDragTarget(folder.id); }}
                      onDragLeave={(e) => { e.stopPropagation(); setFolderDragTarget(null); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDropOnFolder(folder.id); }}
                      style={{ position: 'absolute', inset: 0, zIndex: 2, borderRadius: 20, pointerEvents: draggingSheetId ? 'auto' : 'none' }}
                    />
                  )}
                <Pressable
                  onPress={() => {
                    if (moveTarget) { handleMoveToFolder(folder.id); return; }
                    setActiveFolder(folder.id); setActiveSection('all');
                  }}
                >
                  <View style={[styles.folderCard, {
                    backgroundColor: moveTarget ? `${pillColor}12` : theme.surface,
                    width: cardWidth,
                    borderColor: moveTarget ? pillColor : isDragOver ? pillColor : theme.border,
                    borderWidth: moveTarget || isDragOver ? 2.5 : 1,
                    ...Platform.select({ web: { boxShadow: moveTarget ? `0 0 0 3px ${pillColor}50, 0 4px 24px rgba(0,0,0,0.12)` : isDragOver ? `0 0 0 3px ${pillColor}40, 0 4px 20px rgba(0,0,0,0.08)` : '0 2px 12px rgba(0,0,0,0.06)' } }),
                  }]}>
                    {/* Color banner — height scales with card width so cards are square */}
                    <View style={[styles.folderCardBanner, {
                      backgroundColor: pillColor + '22',
                      height: cardWidth ? Math.round(cardWidth * 0.78) : 220,
                    }]}>
                      <BigFolderIcon color={pillColor} size={cardScale === 'xl' ? 140 : cardScale === 'large' ? 96 : cardScale === 'small' ? 44 : 62} />
                      {(isDragOver || moveTarget) && (
                        <View style={[styles.folderDropHint, { backgroundColor: pillColor + '33', borderColor: pillColor }]}>
                          <Text style={{ color: pillColor, fontSize: 12, fontWeight: '600' }}>
                            {moveTarget ? `Move "${moveTarget.title?.slice(0, 18)}…" here` : 'Drop here'}
                          </Text>
                        </View>
                      )}
                    </View>
                    {/* Info */}
                    <View style={[styles.folderCardInfo, cardScale === 'xl' && { padding: 24 }]}>
                      <Text style={[styles.folderCardName, { color: theme.textPrimary, fontSize: cardScale === 'xl' ? 26 : 18 }]} numberOfLines={1}>{folder.name}</Text>
                      <Text style={[styles.folderCardCount, { color: theme.textMuted, fontSize: cardScale === 'xl' ? 18 : 15 }]}>{count} {count === 1 ? 'item' : 'items'}</Text>
                    </View>
                    {/* Folder action buttons */}
                    <View style={{ position: 'absolute', top: 8, right: 8, flexDirection: 'row', gap: 8 }}>
                      {/* Star / favourite */}
                      <Pressable
                        style={[styles.folderCardEditBtn, { position: 'relative', top: 0, right: 0, backgroundColor: folder.isFavorite ? theme.primary + '20' : 'transparent' }]}
                        onPress={(e) => {
                          e.stopPropagation?.();
                          toggleFolderFavorite(folder.id, folder.isFavorite);
                        }}
                      >
                        <StarIcon color={folder.isFavorite ? theme.primary : theme.textMuted} size={22} filled={folder.isFavorite} />
                      </Pressable>
                      {/* Edit */}
                      <Pressable
                        style={[styles.folderCardEditBtn, { position: 'relative', top: 0, right: 0 }]}
                        onPress={(e) => {
                          e.stopPropagation?.();
                          setFolderEditModal({ id: folder.id, name: folder.name, color: folder.color || FOLDER_COLORS[0] });
                        }}
                      >
                        <MoreIcon color={theme.textMuted} size={22} />
                      </Pressable>
                    </View>
                  </View>
                </Pressable>
                </View>
              );
            })}

            {/* Shared with me folder card — only in 'all' and 'shared' modes */}
            {activeSection !== 'favorites' && <Pressable onPress={() => { setActiveSection('shared'); setActiveFolder(null); }}>
              <View style={[styles.folderCard, {
                width: cardWidth,
                borderColor: activeSection === 'shared' ? '#6B9EBF' : theme.border,
                borderWidth: activeSection === 'shared' ? 2 : 1,
                backgroundColor: theme.surface,
                ...Platform.select({ web: { boxShadow: activeSection === 'shared' ? '0 0 0 3px #6B9EBF40, 0 4px 20px rgba(0,0,0,0.08)' : '0 2px 12px rgba(0,0,0,0.06)' } }),
              }]}>
                <View style={[styles.folderCardBanner, { backgroundColor: '#6B9EBF22', height: cardWidth ? Math.round(cardWidth * 0.78) : 220 }]}>
                  <UsersIcon color="#6B9EBF" size={cardScale === 'xl' ? 140 : cardScale === 'large' ? 96 : cardScale === 'small' ? 44 : 62} />
                  {pendingShares.length > 0 && (
                    <View style={[styles.folderDropHint, { backgroundColor: '#6B9EBF33', borderColor: '#6B9EBF' }]}>
                      <Text style={{ color: '#6B9EBF', fontSize: 12, fontWeight: '600' }}>{pendingShares.length} pending</Text>
                    </View>
                  )}
                </View>
                <View style={[styles.folderCardInfo, cardScale === 'xl' && { padding: 24 }]}>
                  <Text style={[styles.folderCardName, { color: theme.textPrimary, fontSize: cardScale === 'xl' ? 26 : 18 }]}>Shared with me</Text>
                  <Text style={[styles.folderCardCount, { color: theme.textMuted, fontSize: cardScale === 'xl' ? 18 : 15 }]}>
                    {sharedWithMe.length} {sharedWithMe.length === 1 ? 'item' : 'items'}
                  </Text>
                </View>
              </View>
            </Pressable>}

            {/* New Folder card — only in 'all' mode */}
            {activeSection === 'all' && <Pressable onPress={openNewFolderModal}>
              <View style={[styles.folderCard, styles.newFolderCard, {
                width: cardWidth,
                borderColor: theme.border,
                backgroundColor: theme.surface,
              }]}>
                <View style={[styles.folderCardBanner, { backgroundColor: theme.backgroundAlt, height: cardWidth ? Math.round(cardWidth * 0.78) : 220 }]}>
                  <View style={[styles.newFolderIconCircle, { borderColor: theme.primary, width: 72, height: 72, borderRadius: 36 }]}>
                    <PlusIcon color={theme.primary} size={32} />
                  </View>
                </View>
                <View style={[styles.folderCardInfo, cardScale === 'xl' && { padding: 24 }]}>
                  <Text style={[styles.folderCardName, { color: theme.primary, fontSize: cardScale === 'xl' ? 26 : 18 }]}>New Folder</Text>
                </View>
              </View>
            </Pressable>}
          </View>
        )}

        {/* Shared With Me Section */}
        {activeSection === 'shared' && (
          <>
            {pendingShares.length > 0 && (
              <View style={[styles.pendingSharesBanner, { backgroundColor: theme.primary + '15', borderColor: theme.primary }]}>
                <UsersIcon color={theme.primary} size={16} />
                <Text style={[styles.pendingSharesText, { color: theme.primary }]}>
                  {pendingShares.length} pending share invite{pendingShares.length > 1 ? 's' : ''}
                </Text>
                <Pressable onPress={() => setShowNotifications(true)} style={[styles.pendingSharesBtn, { backgroundColor: theme.primary }]}>
                  <Text style={{ color: '#fff', fontSize: 13, fontWeight: '600' }}>Review</Text>
                </Pressable>
              </View>
            )}
            {sharedCards.length === 0 ? (
              <View style={styles.emptyState}>
                <View style={[styles.emptyIcon, { backgroundColor: theme.surface }]}>
                  <UsersIcon color={theme.primary} size={40} />
                </View>
                <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>Nothing shared with you yet</Text>
                <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>When someone shares a sheet or folder with you, it appears here.</Text>
              </View>
            ) : (
              <View style={[styles.sheetsGrid, { gap: isMobile ? 16 : 24 }]}>
                {sharedCards.map((share) => (
                  <SheetCard
                    key={share.id}
                    sheet={{ id: share.resourceId, title: share.resourceTitle, fileType: share.resourceType }}
                    onPress={() => {
                      if (share.type === 'sheet') {
                        navigation.navigate('Viewer', { sheetId: share.resourceId });
                      }
                    }}
                    onFavorite={() => {}}
                    theme={theme}
                    isMobile={isMobile}
                    cardWidth={cardWidth}
                    readOnly
                  />
                ))}
              </View>
            )}
          </>
        )}

        {/* Sheets Grid */}
          {activeSection !== 'shared' && (loading ? (
            <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.primary} />
            </View>
          ) : filteredSheets.length === 0 && !(
              !activeFolder && !searchQuery &&
              (activeSection === 'all' || (activeSection === 'favorites' && allFolders.some(f => f.isFavorite)))
            ) ? (
            <View style={styles.emptyState}>
              <View style={[styles.emptyIcon, { backgroundColor: theme.surface }]}>
                <MusicNoteIcon color={theme.primary} size={40} />
              </View>
              <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>
                {searchQuery ? 'No matches found'
                  : activeSection === 'favorites' ? 'No favorites yet'
                  : activeFolder ? 'No sheets in this folder'
                  : 'No sheets yet'}
              </Text>
              <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
                {searchQuery ? 'Try a different search'
                  : activeSection === 'favorites' ? 'Star a sheet or folder to add it here'
                  : activeFolder ? 'Drag a sheet onto this folder or upload here'
                  : 'Upload your first sheet to get started'}
              </Text>
            </View>
          ) : filteredSheets.length > 0 ? (
          <View style={[styles.sheetsGrid, { gap: isMobile ? 16 : 24 }]}>
              {filteredSheets.map((sheet) => (
                sheet.type === 'link' ? (
                  <LinkCard
                    key={sheet.id}
                    sheet={sheet}
                    onPress={(s) => navigation.navigate('Embed', { sheet: s })}
                    onLongPress={(s) => setMoveTarget({ ...s, fromLongPress: true })}
                    onFavorite={() => toggleFavorite(sheet.id, sheet.isFavorite)}
                    onDelete={handleDeleteSheet}
                    onMove={(s) => setMoveTarget(s)}
                    theme={theme}
                    isMobile={isMobile}
                    cardWidth={cardWidth}
                  />
                ) : (
                  <SheetCard
                    key={sheet.id}
                    sheet={sheet}
                    onPress={(s) => navigation.navigate('Viewer', { sheetId: s.id, sheetData: s })}
                    onLongPress={(s) => setMoveTarget({ ...s, fromLongPress: true })}
                    onFavorite={() => toggleFavorite(sheet.id, sheet.isFavorite)}
                    onShare={handleShareSheet}
                    onDelete={handleDeleteSheet}
                    onShareWithUser={(s) => setShareTarget({ type: 'sheet', id: s.id, title: s.title })}
                    onMove={(s) => setMoveTarget(s)}
                    onPrint={handlePrintSheet}
                    theme={theme}
                    isMobile={isMobile}
                    onDragStart={setDraggingSheetId}
                    cardWidth={cardWidth}
                    isXL={cardScale === 'xl'}
                  />
                )
              ))}
            </View>
          ) : null)}
      </ScrollView>

      {/* Folder Edit Modal */}
      {folderEditModal && (
        <Pressable
          style={[styles.modalOverlay, { backgroundColor: theme.overlay }]}
          onPress={() => setFolderEditModal(null)}
        >
          <Pressable
            style={[styles.modal, { backgroundColor: theme.surface, maxWidth: 400 }]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.textPrimary }]}>
                {folderEditModal?.id === null ? 'New Folder' : 'Edit Folder'}
              </Text>
              <Pressable onPress={() => setFolderEditModal(null)} style={styles.modalClose}>
                <CloseIcon color={theme.textMuted} size={22} />
              </Pressable>
            </View>

            <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Name</Text>
            <TextInput
              style={[styles.modalInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary, marginBottom: 20 }]}
              value={folderEditModal.name}
              onChangeText={(text) => setFolderEditModal({ ...folderEditModal, name: text })}
              placeholder="Folder name"
              placeholderTextColor={theme.textMuted}
              autoFocus
            />

            <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Color</Text>
            <View style={styles.colorSwatchRow}>
              {FOLDER_COLORS.map((c) => (
                <Pressable
                  key={c}
                  style={[styles.colorSwatch, {
                    backgroundColor: c,
                    borderWidth: folderEditModal.color === c ? 3 : 0,
                    borderColor: theme.textPrimary,
                  }]}
                  onPress={() => setFolderEditModal({ ...folderEditModal, color: c })}
                />
              ))}
            </View>

            {/* Preview */}
            <View style={[styles.folderEditPreview, { borderColor: folderEditModal.color }]}>
              <View style={[styles.folderColorDot, { backgroundColor: folderEditModal.color }]} />
              <Text style={[styles.folderPillText, { color: theme.textPrimary }]}>{folderEditModal.name || 'Folder name'}</Text>
            </View>

            <View style={styles.modalActions}>
              <AnimatedButton
                onPress={() => setFolderEditModal(null)}
                style={[styles.modalCancelBtn, { borderColor: theme.border }]}
              >
                <Text style={[styles.modalCancelText, { color: theme.textSecondary }]}>Cancel</Text>
              </AnimatedButton>
              <AnimatedButton
                disabled={!folderEditModal.name.trim()}
                onPress={async () => {
                  const name = folderEditModal.name.trim();
                  const color = folderEditModal.color;
                  if (folderEditModal.id === null) {
                    await handleCreateFolder(name, color);
                  } else {
                    await handleRenameFolder(folderEditModal.id, name);
                    await handleRecolorFolder(folderEditModal.id, color);
                  }
                  setFolderEditModal(null);
                }}
                style={[styles.modalSubmitBtn, { backgroundColor: folderEditModal.color, opacity: folderEditModal.name.trim() ? 1 : 0.5 }]}
              >
                <Text style={[styles.modalSubmitText, { color: '#fff' }]}>
                  {folderEditModal.id === null ? 'Create' : 'Save'}
                </Text>
              </AnimatedButton>
            </View>
          </Pressable>
        </Pressable>
      )}

      {/* Upload Modal - Enhanced with dropdowns */}
      {showUploadModal && (
        <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={() => setShowUploadModal(false)}>
          <Pressable style={[styles.modal, { backgroundColor: theme.surface }]} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.textPrimary }]}>Upload Sheet Music</Text>
              <Pressable onPress={() => setShowUploadModal(false)} style={styles.modalClose}>
                <CloseIcon color={theme.textMuted} size={22} />
              </Pressable>
            </View>
            
            {/* Title - Regular input */}
            <View style={[styles.modalField, { zIndex: 50 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Title *</Text>
              <TextInput
                style={[styles.modalInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
                value={uploadMetadata.title}
                onChangeText={(text) => setUploadMetadata({ ...uploadMetadata, title: text })}
                placeholder="Enter sheet title"
                placeholderTextColor={theme.textMuted}
              />
            </View>

            {/* Composer - Regular input */}
            <View style={[styles.modalField, { zIndex: 40 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Composer</Text>
              <TextInput
                style={[styles.modalInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
                value={uploadMetadata.composer}
                onChangeText={(text) => setUploadMetadata({ ...uploadMetadata, composer: text })}
                placeholder="e.g., Beethoven, Bach"
                placeholderTextColor={theme.textMuted}
              />
            </View>

            {/* Instrument - Combo dropdown */}
            <View style={[styles.modalField, { zIndex: 30 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Instrument</Text>
              <ComboInput
                value={uploadMetadata.instrument}
                onChange={(text) => setUploadMetadata({ ...uploadMetadata, instrument: text })}
                suggestions={INSTRUMENTS}
                placeholder="Select or type instrument"
                theme={theme}
              />
            </View>

            {/* Genre - Combo dropdown */}
            <View style={[styles.modalField, { zIndex: 20 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Genre</Text>
              <ComboInput
                value={uploadMetadata.genre}
                onChange={(text) => setUploadMetadata({ ...uploadMetadata, genre: text })}
                suggestions={GENRES}
                placeholder="Select or type genre"
                theme={theme}
              />
            </View>

            {/* Folder - Folder selector */}
            <View style={[styles.modalField, { zIndex: 10 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary }]}>Folder</Text>
              <FolderSelector
                folders={allFolders.map(f => f.name)}
                selectedFolder={uploadMetadata.folderId ? (allFolders.find(f => f.id === uploadMetadata.folderId)?.name || uploadMetadata.folderId) : null}
                onSelect={(name) => {
                  const folder = allFolders.find(f => f.name === name);
                  setUploadMetadata({ ...uploadMetadata, folderId: folder ? folder.id : name });
                }}
                onCreateNew={handleCreateFolder}
                theme={theme}
              />
            </View>

            <View style={styles.modalActions}>
              <AnimatedButton onPress={() => { setShowUploadModal(false); setUploadFile(null); }} style={[styles.modalCancelBtn, { borderColor: theme.border }]}>
                <Text style={[styles.modalCancelText, { color: theme.textSecondary }]}>Cancel</Text>
              </AnimatedButton>
              <AnimatedButton onPress={handleUpload} disabled={isUploading || !uploadMetadata.title} style={[styles.modalSubmitBtn, { backgroundColor: theme.primary, opacity: (!uploadMetadata.title || isUploading) ? 0.5 : 1 }]}>
                {isUploading ? <ActivityIndicator color={theme.textOnPrimary} /> : (
                  <Text style={[styles.modalSubmitText, { color: theme.textOnPrimary }]}>Upload</Text>
                )}
              </AnimatedButton>
            </View>
          </Pressable>
        </Pressable>
      )}

      {/* Move to Folder Modal — shown via three-dot menu only (long-press uses inline banner+folders) */}
      {moveTarget && !moveTarget.fromLongPress && (
        <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={() => !isMoving && setMoveTarget(null)}>
          <Pressable style={[styles.modal, { backgroundColor: theme.surface, maxWidth: isMobile ? '94%' : 420 }]} onPress={(e) => e.stopPropagation()}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.textPrimary }]}>Move to Folder</Text>
              <Pressable onPress={() => setMoveTarget(null)} style={styles.modalClose}>
                <CloseIcon color={theme.textMuted} size={22} />
              </Pressable>
            </View>
            <Text style={[styles.modalLabel, { color: theme.textSecondary, marginBottom: 14 }]} numberOfLines={1}>
              "{moveTarget.title}"
            </Text>

            <ScrollView style={{ maxHeight: 320 }} showsVerticalScrollIndicator={false}>
              {/* Root option */}
              <Pressable
                style={[styles.folderPickerRow, {
                  backgroundColor: !moveTarget.folderId ? theme.primary + '15' : 'transparent',
                  borderColor: !moveTarget.folderId ? theme.primary : theme.border,
                }]}
                onPress={() => handleMoveToFolder(null)}
                disabled={isMoving}
              >
                <View style={[styles.folderPickerDot, { backgroundColor: theme.textMuted }]} />
                <Text style={[styles.folderPickerName, { color: theme.textPrimary, flex: 1 }]}>No folder (root)</Text>
                {!moveTarget.folderId && <Text style={{ color: theme.primary, fontSize: 13, fontWeight: '600' }}>current</Text>}
                {isMoving && <ActivityIndicator size="small" color={theme.primary} />}
              </Pressable>

              {allFolders.map((folder) => {
                const isCurrent = moveTarget.folderId === folder.id;
                return (
                  <Pressable
                    key={folder.id}
                    style={[styles.folderPickerRow, {
                      backgroundColor: isCurrent ? theme.primary + '15' : 'transparent',
                      borderColor: isCurrent ? theme.primary : theme.border,
                    }]}
                    onPress={() => handleMoveToFolder(folder.id)}
                    disabled={isMoving || isCurrent}
                  >
                    <View style={[styles.folderPickerDot, { backgroundColor: folder.color || theme.primary }]} />
                    <Text style={[styles.folderPickerName, { color: theme.textPrimary, flex: 1 }]}>{folder.name}</Text>
                    {isCurrent && <Text style={{ color: theme.primary, fontSize: 13, fontWeight: '600' }}>current</Text>}
                    {isMoving && !isCurrent && <ActivityIndicator size="small" color={theme.primary} />}
                  </Pressable>
                );
              })}
            </ScrollView>

            <Pressable
              style={[styles.modalCancelBtn, { borderColor: theme.border, alignItems: 'center', marginTop: 16 }]}
              onPress={() => setMoveTarget(null)}
            >
              <Text style={[styles.modalCancelText, { color: theme.textSecondary }]}>Cancel</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      )}

      {/* Add YouTube / Link Modal */}
      {showLinkModal && (
        <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={() => setShowLinkModal(false)}>
          <Pressable style={[styles.modal, { backgroundColor: theme.surface, maxWidth: isMobile ? '94%' : 480 }]} onPress={(e) => e.stopPropagation()}>

            {/* Header with YouTube icon */}
            <View style={{ alignItems: 'center', paddingTop: 8, paddingBottom: 20 }}>
              <View style={{ width: 64, height: 64, borderRadius: 18, backgroundColor: '#FF0000', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                {isWeb ? (
                  <svg width={34} height={34} viewBox="0 0 24 24" fill="#fff">
                    <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 0 0 2.12 2.14C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z"/>
                  </svg>
                ) : <Text style={{ color: '#fff', fontSize: 28 }}>▶</Text>}
              </View>
              <Text style={{ color: theme.textPrimary, fontSize: 22, fontWeight: '800', marginBottom: 4 }}>Add a YouTube Video</Text>
              <Text style={{ color: theme.textSecondary, fontSize: 15, textAlign: 'center', maxWidth: 320 }}>
                Go to YouTube, copy the link, then paste it below.
              </Text>
              <Pressable onPress={() => setShowLinkModal(false)} style={[styles.modalClose, { position: 'absolute', top: 0, right: 0 }]}>
                <CloseIcon color={theme.textMuted} size={22} />
              </Pressable>
            </View>

            {/* Step 1 — URL */}
            <View style={[styles.modalField, { zIndex: 30 }]}>
              <Text style={[styles.modalLabel, { color: theme.textSecondary, fontSize: 13, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6 }]}>
                Step 1 — Paste the YouTube link
              </Text>
              <TextInput
                style={[styles.modalInput, {
                  backgroundColor: theme.backgroundAlt,
                  borderColor: linkUrlError ? theme.error : theme.border,
                  color: theme.textPrimary,
                  fontSize: 15,
                  paddingVertical: 14,
                }]}
                value={linkUrl}
                onChangeText={handleLinkUrlChange}
                placeholder="https://youtube.com/watch?v=…"
                placeholderTextColor={theme.textMuted}
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
                keyboardType="url"
              />
              {!!linkUrlError && <Text style={{ color: theme.error, fontSize: 13, marginTop: 6 }}>{linkUrlError}</Text>}
            </View>

            {/* Step 2 — Title */}
            <View style={[styles.modalField, { zIndex: 20 }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <Text style={[styles.modalLabel, { color: theme.textSecondary, fontSize: 13, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 0 }]}>
                  Step 2 — Give it a name
                </Text>
                {isFetchingMeta && <ActivityIndicator size="small" color={theme.primary} />}
                {!isFetchingMeta && linkTitle !== '' && linkTitle !== 'YouTube Video' && (
                  <Text style={{ color: theme.success || '#4CAF50', fontSize: 12, fontWeight: '600' }}>✓ Auto-filled</Text>
                )}
              </View>
              <TextInput
                style={[styles.modalInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary, fontSize: 15, paddingVertical: 14 }]}
                value={linkTitle}
                onChangeText={setLinkTitle}
                placeholder={isFetchingMeta ? 'Fetching title…' : 'e.g. Beethoven Op. 13 play-along'}
                placeholderTextColor={theme.textMuted}
              />
            </View>

            {/* Optional fields */}
            <View style={[styles.modalField, { zIndex: 10 }]}>
              <Text style={[styles.modalLabel, { color: theme.textMuted, fontSize: 12 }]}>Notes (optional)</Text>
              <TextInput
                style={[styles.modalInput, { backgroundColor: theme.backgroundAlt, borderColor: theme.border, color: theme.textPrimary }]}
                value={linkNotes}
                onChangeText={setLinkNotes}
                placeholder="Any extra notes…"
                placeholderTextColor={theme.textMuted}
              />
            </View>

            <View style={[styles.modalField, { zIndex: 5 }]}>
              <Text style={[styles.modalLabel, { color: theme.textMuted, fontSize: 12 }]}>Save to folder (optional)</Text>
              <FolderSelector
                folders={allFolders.map(f => f.name)}
                selectedFolder={linkFolderId ? (allFolders.find(f => f.id === linkFolderId)?.name || null) : null}
                onSelect={(name) => {
                  const f = allFolders.find(f => f.name === name);
                  setLinkFolderId(f?.id || null);
                }}
                onCreateNew={handleCreateFolder}
                theme={theme}
              />
            </View>

            {/* Actions */}
            <View style={[styles.modalActions, { marginTop: 8 }]}>
              <AnimatedButton onPress={() => setShowLinkModal(false)} style={[styles.modalCancelBtn, { borderColor: theme.border }]}>
                <Text style={[styles.modalCancelText, { color: theme.textSecondary }]}>Cancel</Text>
              </AnimatedButton>
              <AnimatedButton
                onPress={handleAddLink}
                disabled={isAddingLink || !linkTitle.trim() || !linkUrl.trim()}
                style={[styles.modalSubmitBtn, { backgroundColor: '#FF0000', opacity: (isAddingLink || !linkTitle.trim() || !linkUrl.trim()) ? 0.5 : 1, flex: 1 }]}
              >
                {isAddingLink ? <ActivityIndicator color="#fff" /> : (
                  <Text style={[styles.modalSubmitText, { color: '#fff', fontSize: 16 }]}>Save Video</Text>
                )}
              </AnimatedButton>
            </View>
          </Pressable>
        </Pressable>
      )}

      {/* Share Modal with QR Code */}
      <ShareModal
        visible={showShareModal}
        sheet={shareSheet}
        shareLink={shareLink}
        onClose={() => { setShowShareModal(false); setShareSheet(null); setShareLink(''); }}
        onCopy={handleCopyShareLink}
        theme={theme}
        isMobile={isMobile}
      />

      {/* Drag Overlay */}
      {isDragging && (
        <View style={[styles.dragOverlay, { backgroundColor: `${theme.background}F0` }]}>
          <View style={[styles.dragContent, { borderColor: theme.primary }]}>
            <UploadIcon color={theme.primary} size={48} />
            <Text style={[styles.dragText, { color: theme.textPrimary }]}>Drop to upload</Text>
          </View>
        </View>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <Pressable style={[styles.modalOverlay, { backgroundColor: theme.overlay }]} onPress={() => !isDeleting && setDeleteTarget(null)}>
          <Pressable style={[styles.modal, { backgroundColor: theme.surface, maxWidth: isMobile ? '92%' : 400 }]} onPress={(e) => e.stopPropagation()}>
            <View style={[styles.deleteModalIcon, { backgroundColor: theme.error + '15' }]}>
              <TrashIcon color={theme.error} size={28} />
            </View>
            <Text style={[styles.deleteModalTitle, { color: theme.textPrimary }]}>Delete sheet?</Text>
            <Text style={[styles.deleteModalBody, { color: theme.textSecondary }]}>
              "<Text style={{ fontWeight: '700', color: theme.textPrimary }}>{deleteTarget.title}</Text>" will be permanently deleted and free up a slot in your library.
            </Text>
            {!user?.isPro && remainingUploads <= 2 && (
              <View style={[styles.deleteModalHint, { backgroundColor: theme.primary + '12', borderColor: theme.primary }]}>
                <Text style={[{ color: theme.primary, fontSize: 13, fontWeight: '600' }]}>
                  This will free up 1 slot ({remainingUploads + 1} of {FREE_TIER.maxSheets} available after).
                </Text>
              </View>
            )}
            <View style={styles.deleteModalActions}>
              <Pressable
                style={[styles.deleteModalCancel, { backgroundColor: theme.backgroundAlt, borderColor: theme.border }]}
                onPress={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                <Text style={[{ color: theme.textPrimary, fontWeight: '600', fontSize: 15 }]}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.deleteModalConfirm, { backgroundColor: theme.error, opacity: isDeleting ? 0.7 : 1 }]}
                onPress={confirmDeleteSheet}
                disabled={isDeleting}
              >
                {isDeleting
                  ? <ActivityIndicator color="#fff" size="small" />
                  : <Text style={[{ color: '#fff', fontWeight: '700', fontSize: 15 }]}>Delete</Text>}
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      )}

      {/* User Share Modal */}
      <UserShareModal
        visible={!!shareTarget}
        target={shareTarget}
        onClose={() => setShareTarget(null)}
        theme={theme}
        isMobile={isMobile}
      />

      {/* Notifications Panel */}
      <NotificationsPanel
        visible={showNotifications}
        onClose={() => setShowNotifications(false)}
        theme={theme}
        isMobile={isMobile}
        respondToConnection={respondToConnection}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  moveBanner: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 14, gap: 12, zIndex: 200 },
  moveBannerTitle: { color: '#fff', fontWeight: '700', fontSize: 15 },
  moveBannerSub: { color: 'rgba(255,255,255,0.8)', fontSize: 13, marginTop: 2 },
  moveBannerCancel: { padding: 8 },
  scrollContent: { paddingTop: 120, paddingBottom: 60 },

  // Top Search
  topSearchContainer: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 20, paddingVertical: 16, borderRadius: 999, borderWidth: 1.5 },
  topSearchInput: { flex: 1, fontSize: 16, ...Platform.select({ web: { outlineStyle: 'none' } }) },

  // Share Modal
  shareModal: { borderRadius: 28, padding: 32, width: '100%', ...Platform.select({ web: { boxShadow: '0 16px 48px rgba(0,0,0,0.2)' } }) },
  shareModalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  shareModalTitle: { fontSize: 22, fontWeight: '700' },
  qrContainer: { alignItems: 'center', marginBottom: 24 },
  qrHint: { fontSize: 13, marginTop: 12 },
  shareLinkContainer: { flexDirection: 'row', alignItems: 'center', padding: 16, borderRadius: 16, borderWidth: 1, marginBottom: 20 },
  shareLinkText: { flex: 1, fontSize: 14, fontFamily: 'monospace' },
  shareActions: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  shareActionBtn: { flex: 1, padding: 16, borderRadius: 999, alignItems: 'center' },
  shareActionText: { fontSize: 16, fontWeight: '600' },
  shareNote: { fontSize: 13, textAlign: 'center' },

  // Navbar
  navbarContainer: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, paddingTop: 20 },
  navbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 999, paddingHorizontal: 20, paddingVertical: 14 },
  logoContainer: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  logoIcon: { width: 40, height: 40, borderRadius: 14, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  logoNote: { width: 9, height: 9, borderRadius: 5, position: 'absolute', top: 11, left: 11 },
  logoNote2: { width: 7, height: 7, borderRadius: 4, position: 'absolute', bottom: 11, right: 11 },
  logoText: { fontSize: 20, fontWeight: '700' },
  navLinks: { flexDirection: 'row', gap: 8 },
  navRight: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  darkModeToggle: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  navLink: { paddingHorizontal: 16, paddingVertical: 10, borderRadius: 999 },
  navLinkActive: { borderRadius: 999 },
  navLinkText: { fontSize: 15, fontWeight: '500' },
  userMenuContainer: { position: 'relative' },
  userAvatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  userAvatarText: { fontSize: 16, fontWeight: '700' },
  userMenu: { position: 'absolute', top: 50, right: 0, width: 240, borderRadius: 16, borderWidth: 1, ...Platform.select({ web: { boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }), zIndex: 200 },
  userMenuHeader: { padding: 16, borderBottomWidth: 1 },
  userMenuName: { fontSize: 16, fontWeight: '600', marginBottom: 2 },
  userMenuEmail: { fontSize: 13 },
  planBadge: { marginTop: 8, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, alignSelf: 'flex-start' },
  userMenuItem: { flexDirection: 'row', alignItems: 'center', padding: 14 },
  userMenuItemText: { fontSize: 15 },
  userMenuDivider: { height: 1, marginHorizontal: 14 },

  // Combo Input / Dropdown
  comboContainer: { position: 'relative', zIndex: 50 },
  comboInputRow: { flexDirection: 'row', alignItems: 'center', borderRadius: 16, borderWidth: 1, paddingRight: 12 },
  comboTextInput: { flex: 1, padding: 16, fontSize: 16, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  comboArrow: { padding: 8 },
  comboDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, borderRadius: 12, borderWidth: 1, marginTop: 4, zIndex: 9999, ...Platform.select({ web: { boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }) },
  comboOption: { padding: 14, borderBottomWidth: 1 },
  comboOptionText: { fontSize: 15 },
  
  // Folder Selector
  folderSelector: { flexDirection: 'row', alignItems: 'center', borderRadius: 16, borderWidth: 1, padding: 16 },
  folderText: { flex: 1, fontSize: 16 },
  newFolderRow: { flexDirection: 'row', alignItems: 'center', padding: 10, borderTopWidth: 1, gap: 8 },
  newFolderInput: { flex: 1, padding: 10, borderRadius: 8, borderWidth: 1, fontSize: 14, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  newFolderBtn: { paddingHorizontal: 14, paddingVertical: 10, borderRadius: 8 },

  // Folder Pills
  folderPillsScroll: { marginBottom: 24 },
  folderPillsContainer: { gap: 10, paddingVertical: 4 },
  folderPill: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 18, paddingVertical: 12, borderRadius: 999, borderWidth: 1 },
  addFolderPill: { borderStyle: 'dashed' },
  folderPillText: { fontSize: 16, fontWeight: '600' },
  folderColorDot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  folderMenuBtn: { padding: 2, marginLeft: 2 },
  folderContextMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    minWidth: 160,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 4,
    zIndex: 500,
    ...Platform.select({ web: { boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }),
  },
  folderContextMenuItem: { paddingHorizontal: 16, paddingVertical: 12 },
  folderContextMenuText: { fontSize: 14 },
  // Color picker
  colorSwatchRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 },
  colorSwatch: { width: 32, height: 32, borderRadius: 999 },
  folderEditPreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 9,
    borderRadius: 999,
    borderWidth: 2,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },

  // Welcome
  welcomeSection: { marginBottom: 36 },
  welcomeText: { fontSize: 20, marginBottom: 6 },
  userName: { fontWeight: '700' },

  // Stats
  statsRow: { gap: 16, marginBottom: 36 },
  statCard: { flex: 1, padding: 24, borderRadius: 24, ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' } }) },
  statValue: { fontSize: 42, fontWeight: '700', marginBottom: 4 },
  statLabel: { fontSize: 17 },

  // Drop Zone
  dropZone: { borderWidth: 2, borderStyle: 'dashed', borderRadius: 24, padding: 32, alignItems: 'center', marginBottom: 16 },
  addLinkBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderStyle: 'dashed', borderRadius: 12, paddingHorizontal: 16, paddingVertical: 10, alignSelf: 'flex-start', marginBottom: 24 },
  addLinkBtnText: { fontSize: 14, fontWeight: '500' },
  uploadIconContainer: { width: 52, height: 52, borderRadius: 16, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  dropZoneTitle: { fontWeight: '700', marginBottom: 6 },
  dropZoneSubtitle: { fontSize: 15, marginBottom: 4 },
  dropZoneFormats: { fontSize: 13, marginBottom: 12 },
  uploadLimit: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 999 },
  uploadLimitText: { fontSize: 12 },

  // Section Pills
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 },
  sectionPills: { gap: 12 },
  sectionPill: { paddingHorizontal: 24, paddingVertical: 14, borderRadius: 999 },
  sectionPillText: { fontSize: 17, fontWeight: '600' },

  // Search
  searchContainer: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderRadius: 999, borderWidth: 1, minWidth: 200 },
  searchInput: { flex: 1, fontSize: 15, ...Platform.select({ web: { outlineStyle: 'none' } }) },

  // Sheets Grid
  sheetsGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  sheetCard: { borderRadius: 20, overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.06)' } }) },
  sheetThumbnail: { height: 220, position: 'relative', justifyContent: 'center', alignItems: 'center', borderRadius: 12, margin: 8, backgroundColor: '#fff' },
  // Preview styles
  pdfPreview: { flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' },
  pdfLabel: { fontSize: 12, fontWeight: '700', marginTop: 8 },
  pageCount: { fontSize: 11, marginTop: 2 },
  fallbackPreview: { flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' },
  imagePlaceholder: { flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' },
  favoriteIndicator: { position: 'absolute', top: 8, left: 8, width: 24, height: 24, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  // Legacy styles (can be removed)
  staffLine: { height: 1 },
  notePreview: { position: 'absolute', width: 12, height: 10, borderRadius: 6 },
  sheetInfo: { padding: 18 },
  sheetTitle: { fontSize: 19, fontWeight: '700', marginBottom: 5 },
  sheetComposer: { fontSize: 16, marginBottom: 12 },
  sheetMeta: { flexDirection: 'row', gap: 8 },
  metaTag: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999 },
  metaTagText: { fontSize: 14 },
  sheetActions: { position: 'absolute', top: 10, right: 10, flexDirection: 'row', gap: 8 },
  actionBtn: { padding: 12, borderRadius: 12 },
  shareIndicator: { position: 'absolute', bottom: 8, right: 8, width: 24, height: 24, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },
  sheetMenu: { position: 'absolute', top: 60, right: 10, width: 180, borderRadius: 14, borderWidth: 1, ...Platform.select({ web: { boxShadow: '0 4px 20px rgba(0,0,0,0.16)' } }), zIndex: 100 },
  sheetMenuItem: { flexDirection: 'row', alignItems: 'center', padding: 16 },
  sheetMenuText: { fontSize: 16, fontWeight: '500' },
  sheetMenuDivider: { height: 1, marginHorizontal: 8 },

  // Empty State
  emptyState: { alignItems: 'center', paddingVertical: 80 },
  emptyIcon: { width: 100, height: 100, borderRadius: 28, justifyContent: 'center', alignItems: 'center', marginBottom: 24 },
  emptyTitle: { fontSize: 20, fontWeight: '700', marginBottom: 8 },
  emptySubtitle: { fontSize: 16, textAlign: 'center', maxWidth: 280 },

  // Loading
  loadingContainer: { paddingVertical: 80, alignItems: 'center' },

  // Modal
  modalOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', zIndex: 200 },
  modal: { width: '90%', maxWidth: 480, borderRadius: 28, padding: 28, maxHeight: '90%', ...Platform.select({ web: { boxShadow: '0 16px 48px rgba(0,0,0,0.15)', overflow: 'visible' } }) },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  modalTitle: { fontSize: 24, fontWeight: '700' },
  modalClose: { padding: 8 },
  modalField: { marginBottom: 18, zIndex: 10 },
  modalLabel: { fontSize: 14, fontWeight: '600', marginBottom: 10 },
  modalInput: { borderRadius: 16, padding: 16, fontSize: 16, borderWidth: 1, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12, marginTop: 24 },
  modalCancelBtn: { paddingHorizontal: 24, paddingVertical: 14, borderRadius: 999, borderWidth: 1.5 },
  modalCancelText: { fontSize: 16, fontWeight: '500' },
  modalSubmitBtn: { paddingHorizontal: 28, paddingVertical: 14, borderRadius: 999 },
  modalSubmitText: { fontSize: 16, fontWeight: '600' },

  // Drag Overlay
  dragOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  dragContent: { borderWidth: 3, borderStyle: 'dashed', borderRadius: 32, padding: 60, alignItems: 'center' },
  dragText: { fontSize: 24, fontWeight: '700', marginTop: 20 },

  // Limit-reached drop zone
  dropZoneLimitReached: { borderStyle: 'dashed', borderWidth: 2 },
  limitActionBtn: { paddingHorizontal: 20, paddingVertical: 12, borderRadius: 999, alignItems: 'center' },

  // Delete confirmation modal
  deleteModalIcon: { width: 60, height: 60, borderRadius: 30, justifyContent: 'center', alignItems: 'center', alignSelf: 'center', marginBottom: 16 },
  deleteModalTitle: { fontSize: 20, fontWeight: '700', textAlign: 'center', marginBottom: 10 },
  deleteModalBody: { fontSize: 15, lineHeight: 22, textAlign: 'center', marginBottom: 16 },
  deleteModalHint: { borderWidth: 1.5, borderRadius: 12, padding: 12, marginBottom: 16, alignItems: 'center' },
  deleteModalActions: { flexDirection: 'row', gap: 12 },
  deleteModalCancel: { flex: 1, paddingVertical: 15, borderRadius: 999, alignItems: 'center', borderWidth: 1.5 },
  deleteModalConfirm: { flex: 1, paddingVertical: 15, borderRadius: 999, alignItems: 'center' },
  folderPickerRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 14, paddingVertical: 13, borderRadius: 12, borderWidth: 1, marginBottom: 8 },
  folderPickerDot: { width: 12, height: 12, borderRadius: 6 },
  folderPickerName: { fontSize: 15, fontWeight: '500' },

  // Notification bell badge
  bellBadge: { position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4, zIndex: 10 },
  bellBadgeText: { color: '#fff', fontSize: 10, fontWeight: '700' },

  // User Share Modal
  shareTargetLabel: { fontSize: 13, marginTop: 2 },
  shareSearchRow: { flexDirection: 'row', alignItems: 'center', borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 8 },
  shareSearchInput: { flex: 1, padding: 14, fontSize: 15, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  shareSearchBtn: { paddingHorizontal: 18, paddingVertical: 14 },
  shareError: { fontSize: 13, marginBottom: 8 },
  userResultRow: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 16, borderWidth: 1, marginBottom: 8 },
  userResultAvatar: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  sendInviteBtn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 999 },
  sentConfirm: { padding: 14, borderRadius: 14, borderWidth: 1.5, marginBottom: 8 },
  existingShareRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, gap: 10 },
  revokeBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1.5 },

  // Notifications Panel
  notifPanel: { borderRadius: 24, width: '100%', overflow: 'hidden', ...Platform.select({ web: { boxShadow: '0 16px 48px rgba(0,0,0,0.2)' } }) },
  notifHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1 },
  notifTitle: { fontSize: 20, fontWeight: '700' },
  markAllBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1 },
  notifItem: { flexDirection: 'row', alignItems: 'flex-start', padding: 16, borderBottomWidth: 1, gap: 10 },
  notifDot: { width: 8, height: 8, borderRadius: 4, marginTop: 6, flexShrink: 0 },
  notifText: { fontSize: 14, lineHeight: 20, marginBottom: 8 },
  notifTime: { fontSize: 12 },
  notifActions: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  notifActionBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 999 },

  // Pending shares banner
  pendingSharesBanner: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderRadius: 16, borderWidth: 1.5, marginBottom: 20 },
  pendingSharesText: { flex: 1, fontSize: 14, fontWeight: '600' },
  pendingSharesBtn: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 999 },

  // Icons (replacing emojis)
  folderIcon: { width: 18, height: 14, borderWidth: 2, borderRadius: 3, marginRight: 8 },
  folderIconSmall: { width: 14, height: 10, borderWidth: 1.5, borderRadius: 2, marginRight: 6 },
  searchIcon: { width: 16, height: 16, borderWidth: 2, borderRadius: 8, marginRight: 4 },
  musicNoteIcon: { width: 24, height: 24, borderRadius: 12 },
  dropFileIcon: { width: 50, height: 60, borderWidth: 3, borderRadius: 8 },

  // Toolbar row with search
  toolbarRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 20, zIndex: 50 },
  toolbarSearch: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 999, borderWidth: 1.5 },
  toolbarSearchInput: { flex: 1, fontSize: 14, minWidth: 120, ...Platform.select({ web: { outlineStyle: 'none' } }) },

  // Toolbar (sort / filter / scale)
  toolbarBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 14, paddingVertical: 9,
    borderRadius: 999, borderWidth: 1.5,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  toolbarBtnText: { fontSize: 13, fontWeight: '600' },
  toolbarDropdown: {
    position: 'absolute', top: '110%', right: 0,
    minWidth: 160, borderRadius: 14, borderWidth: 1,
    marginTop: 4, zIndex: 9999,
    ...Platform.select({ web: { boxShadow: '0 8px 24px rgba(0,0,0,0.13)' } }),
  },
  toolbarDropdownItem: { paddingHorizontal: 16, paddingVertical: 11, borderRadius: 8 },
  toolbarDropdownText: { fontSize: 14, fontWeight: '500' },
  filterLabel: { fontSize: 12, fontWeight: '600', marginBottom: 6 },
  filterPill: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1.5 },
  filterPillText: { fontSize: 13, fontWeight: '500' },
  filterChip: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1 },
  scaleToggle: { flexDirection: 'row', borderRadius: 999, borderWidth: 1.5, overflow: 'hidden' },
  scaleBtn: { paddingHorizontal: 14, paddingVertical: 10 },
  scaleBtnText: { fontSize: 14, fontWeight: '700' },

  // Folder Card
  folderCard: {
    borderRadius: 20, overflow: 'hidden', borderWidth: 1,
    ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.06)', cursor: 'pointer' } }),
  },
  newFolderCard: { borderStyle: 'dashed' },
  folderCardBanner: {
    height: 220, justifyContent: 'center', alignItems: 'center', position: 'relative',
  },
  folderDropHint: {
    position: 'absolute', bottom: 8, left: '50%',
    transform: [{ translateX: -40 }],
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 999, borderWidth: 1.5,
    width: 80, alignItems: 'center',
  },
  folderCardInfo: { padding: 14 },
  folderCardName: { fontSize: 18, fontWeight: '700', marginBottom: 4 },
  folderCardCount: { fontSize: 15 },
  folderCardEditBtn: { position: 'absolute', top: 10, right: 10, padding: 10, borderRadius: 10 },
  newFolderIconCircle: { width: 52, height: 52, borderRadius: 26, borderWidth: 2, borderStyle: 'dashed', justifyContent: 'center', alignItems: 'center' },
});

export default LibraryScreen;
